import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Chessboard } from 'react-chessboard';
import { fetchStepAnnotations, fetchStepAnnotationsByLessonAndOrder } from '../../lib/supabaseAnnotations';
import {
  upsertLessonProgress,
  upsertLearningPathState,
  insertAssessmentResult
} from '../../lib/supabaseProgress';
import {
  getTTSEnabledDefault,
  setTTSEnabled,
  stopTTS
} from '../../lib/tts';
import { playErrorSound, playMoveSound, playSuccessSound } from '../../lib/sfx';
import { getPieceAt, getPseudoLegalMovesForSquare, parseFen, coordsToSquare } from '../../lib/fenMoves';
import { useChessStepEngine } from '../../lib/useChessStepEngine';
import SuccessBurst from './SuccessBurst';

function cleanLessonText(value) {
  const raw = String(value ?? '');
  if (!raw) return '';
  return raw
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function resolveLocalizedText(value, language) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return cleanLessonText(value);
  }
  if (typeof value === 'object') {
    // Common payload style: { text_ro, text_en }
    const byTextKey =
      language === 'ro'
        ? value?.text_ro ?? value?.text_en
        : value?.text_en ?? value?.text_ro;
    if (typeof byTextKey === 'string' || typeof byTextKey === 'number' || typeof byTextKey === 'boolean') {
      return cleanLessonText(byTextKey);
    }

    const langValue = value?.[language];
    if (typeof langValue === 'string' || typeof langValue === 'number' || typeof langValue === 'boolean') {
      return cleanLessonText(langValue);
    }
    // Nested object at language key, e.g. { ro: { text: "..." } }
    if (langValue && typeof langValue === 'object') {
      const nestedLangText = langValue?.text ?? langValue?.value ?? langValue?.label;
      if (
        typeof nestedLangText === 'string' ||
        typeof nestedLangText === 'number' ||
        typeof nestedLangText === 'boolean'
      ) {
        return cleanLessonText(nestedLangText);
      }
    }
    const roValue = value?.ro;
    if (typeof roValue === 'string' || typeof roValue === 'number' || typeof roValue === 'boolean') {
      return cleanLessonText(roValue);
    }
    if (roValue && typeof roValue === 'object') {
      const nestedRoText = roValue?.text ?? roValue?.value ?? roValue?.label;
      if (
        typeof nestedRoText === 'string' ||
        typeof nestedRoText === 'number' ||
        typeof nestedRoText === 'boolean'
      ) {
        return cleanLessonText(nestedRoText);
      }
    }
    const enValue = value?.en;
    if (typeof enValue === 'string' || typeof enValue === 'number' || typeof enValue === 'boolean') {
      return cleanLessonText(enValue);
    }
    if (enValue && typeof enValue === 'object') {
      const nestedEnText = enValue?.text ?? enValue?.value ?? enValue?.label;
      if (
        typeof nestedEnText === 'string' ||
        typeof nestedEnText === 'number' ||
        typeof nestedEnText === 'boolean'
      ) {
        return cleanLessonText(nestedEnText);
      }
    }
  }
  return '';
}

function LessonViewer({
  lessonData,
  isLoading,
  error,
  language,
  user,
  trackProgress = true,
  onRequestLogin,
  onClose,
  onPuzzleScored,
  onComplete,
  initialSegment = 0
}) {
  const boardWrapRef = useRef(null);
  const dragSourceRef = useRef('');
  const dragStateRef = useRef({ source: '', target: '', dropHandled: false });
  const [currentSegment, setCurrentSegment] = useState(0);
  const [boardWidth, setBoardWidth] = useState(480);
  // Board position comes from the step FEN; we only keep an override for interactive steps
  // so the piece can remain on the destination square after a correct move.
  const [positionOverride, setPositionOverride] = useState(null);
  const [boardArrows, setBoardArrows] = useState([]);
  const [arrowColor, setArrowColor] = useState('rgb(255,170,0)');
  const [squareStyles, setSquareStyles] = useState({});
  const [baseStepAnnotations, setBaseStepAnnotations] = useState({
    arrows: [],
    arrowColor: 'rgb(255,170,0)',
    squareStyles: {}
  });
  const [selectionSquareStyles, setSelectionSquareStyles] = useState({});
  const [selectedMoveSource, setSelectedMoveSource] = useState('');
  const [transientSquareStyles, setTransientSquareStyles] = useState({});
  const [moveFeedback, setMoveFeedback] = useState(null); // {status:'correct'|'wrong', message:string}
  const [actionFeedback, setActionFeedback] = useState(null); // {status:'ok'|'warn', message:string}
  const [hasAttemptedMove, setHasAttemptedMove] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [focusModeOn, setFocusModeOn] = useState(false);
  const [adjustArmed, setAdjustArmed] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null); // {from,to, text}
  const [sparkRainOn, setSparkRainOn] = useState(false);
  const [sparkRainNonce, setSparkRainNonce] = useState(0);
  const [burstTrigger, setBurstTrigger] = useState(0);
  const [ttsEnabled, setTtsEnabled] = useState(() => getTTSEnabledDefault());
  const [puzzleTries, setPuzzleTries] = useState(0);
  const [puzzleWrongStreak, setPuzzleWrongStreak] = useState(0);
  const [puzzleOutcomeSent, setPuzzleOutcomeSent] = useState(false);
  const [puzzleScoreSubmitted, setPuzzleScoreSubmitted] = useState(false);
  const [puzzleAwardResult, setPuzzleAwardResult] = useState(null);
  const [lastCorrectMoveUci, setLastCorrectMoveUci] = useState('');
  const [solutionVisible, setSolutionVisible] = useState(false);
  const [solutionUnlocked, setSolutionUnlocked] = useState(false);
  const [solutionMoveIndex, setSolutionMoveIndex] = useState(0);
  const [solutionPlaybackPlacement, setSolutionPlaybackPlacement] = useState(null);
  const [guidedMoveIndex, setGuidedMoveIndex] = useState(0);
  const [segmentStartedAt, setSegmentStartedAt] = useState(() => Date.now());
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizSelectedOptionId, setQuizSelectedOptionId] = useState('');
  const [quizChecked, setQuizChecked] = useState(false);
  const [quizCorrectCount, setQuizCorrectCount] = useState(0);
  const [quizAnsweredIds, setQuizAnsweredIds] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [cctSelectedIds, setCctSelectedIds] = useState([]);
  const [cctSubmitted, setCctSubmitted] = useState(false);
  const narrationAudioRef = useRef(null);

  const stopNarrationAudio = useCallback(() => {
    const audio = narrationAudioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // ignore
    }
  }, []);

  const playNarration = useCallback(({ text, audioUrl }) => {
    if (!ttsEnabled) return;

    stopTTS();
    stopNarrationAudio();

    if (audioUrl) {
      try {
        const audio = new Audio(audioUrl);
        narrationAudioRef.current = audio;
        audio.play().catch(() => {
          // If browser blocks autoplay, keep silent (no Web Speech fallback).
        });
        return;
      } catch {
        // Keep silent if audio cannot be created.
      }
    }
  }, [stopNarrationAudio, ttsEnabled]);

  useEffect(() => {
    setCurrentSegment(Math.max(0, Number(initialSegment || 0)));
    setPositionOverride(null);
    setBoardArrows([]);
    setSquareStyles({});
    setBaseStepAnnotations({
      arrows: [],
      arrowColor: 'rgb(255,170,0)',
      squareStyles: {}
    });
    setSelectionSquareStyles({});
    setSelectedMoveSource('');
    setTransientSquareStyles({});
    setMoveFeedback(null);
    setActionFeedback(null);
    setHasAttemptedMove(false);
    setActionCompleted(false);
    setFocusModeOn(false);
    setAdjustArmed(false);
    setPendingConfirm(null);
    setSparkRainOn(false);
    setPuzzleTries(0);
    setPuzzleWrongStreak(0);
    setPuzzleOutcomeSent(false);
    setPuzzleScoreSubmitted(false);
    setPuzzleAwardResult(null);
    setLastCorrectMoveUci('');
    setSolutionVisible(false);
    setSolutionUnlocked(false);
    setSolutionMoveIndex(0);
    setSolutionPlaybackPlacement(null);
    setGuidedMoveIndex(0);
    setGuidedMoveIndex(0);
    setSegmentStartedAt(Date.now());
    setQuizStarted(false);
    setQuizIndex(0);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
    setQuizCorrectCount(0);
    setQuizAnsweredIds({});
    setQuizCompleted(false);
    setCctSelectedIds([]);
    setCctSubmitted(false);
  }, [lessonData?.id, initialSegment]);

  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el) return undefined;

    const updateWidth = () => {
      const w = Math.max(220, Math.min(480, Math.floor(el.clientWidth || 480)));
      setBoardWidth(w);
    };

    updateWidth();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateWidth);
      ro.observe(el);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWidth);
    }

    return () => {
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateWidth);
      }
    };
  }, []);

  const segments = lessonData?.segments || [];
  const segment = segments[currentSegment];
  const legacyInteraction = useMemo(() => {
    const raw = segment?.interaction || null;
    if (!raw) return null;
    const normalize = (value) => {
      if (!value || typeof value !== 'object') return value;
      const out = { ...value };
      const t = String(out.type || '').trim().toLowerCase();
      if (!t) return out;
      if (['move', 'guided_move', 'guidedmove', 'puzzle_move', 'puzzlemove'].includes(t)) {
        out.type = 'move';
      } else if (['action', 'tap_square', 'tap', 'square'].includes(t)) {
        out.type = 'action';
        if (!out.action && t !== 'action') out.action = 'tap_square';
      } else if (['cct_check', 'cct', 'checklist'].includes(t)) {
        out.type = 'cct_check';
      }
      return out;
    };
    if (typeof raw === 'object') return normalize(raw);
    if (typeof raw === 'string') {
      try {
        return normalize(JSON.parse(raw));
      } catch {
        return null;
      }
    }
    return null;
  }, [segment?.interaction]);

  const lessonSlug = String(lessonData?.id || '').trim().toLowerCase();
  const boardOrientation = useMemo(
    () => (lessonSlug.startsWith('b-o2-') ? 'black' : 'white'),
    [lessonSlug]
  );
  const isStepEngineV2 = lessonSlug.startsWith('b-o');
  const stepEngine = useChessStepEngine({
    segment,
    legacyInteraction,
    enabled: isStepEngineV2
  });
  const interaction = stepEngine.interaction;
  // Base FEN is segment.fen; position state may change during interactive steps.

  const toBoardPosition = (fen) => {
    // react-chessboard is happiest with the piece-placement section (first field).
    if (!fen) return 'start';
    const raw = String(fen).trim();
    if (!raw) return 'start';
    if (raw === 'start') return 'start';
    return raw.split(' ')[0];
  };

  const baseBoardPosition = useMemo(() => {
    if (!segment?.fen) return 'start';
    return toBoardPosition(segment.fen);
  }, [segment?.fen]);

  const boardPosition = positionOverride || baseBoardPosition;

  const moveInPlacement = (placement, fromSq, toSq, promotion) => {
    // Minimal move applicator for piece-placement strings (no legality checks).
    if (!placement || !fromSq || !toSq) return placement;
    const ranks = String(placement).split('/');
    if (ranks.length !== 8) return placement;

    const decodeRank = (rankStr) => {
      const out = [];
      for (const ch of rankStr) {
        const n = Number(ch);
        if (Number.isFinite(n) && n > 0) {
          for (let i = 0; i < n; i += 1) out.push(null);
        } else {
          out.push(ch);
        }
      }
      return out.length === 8 ? out : null;
    };

    const board = ranks.map(decodeRank);
    if (board.some((r) => !r)) return placement;

    const fileToX = (f) => 'abcdefgh'.indexOf(f);
    const toCoord = (sq) => {
      const s = String(sq);
      const x = fileToX(s[0]);
      const rank = Number(s[1]);
      const y = 8 - rank;
      if (x < 0 || x > 7 || y < 0 || y > 7) return null;
      return { x, y };
    };

    const from = toCoord(fromSq);
    const to = toCoord(toSq);
    if (!from || !to) return placement;

    const piece = board[from.y][from.x];
    if (!piece) return placement;

    board[from.y][from.x] = null;

    // Handle en passant in placement mode:
    // pawn moves diagonally to an empty square -> captured pawn is on the source rank.
    const isPawn = piece === 'P' || piece === 'p';
    const isDiagonal = from.x !== to.x;
    const targetWasEmpty = board[to.y][to.x] == null;
    if (isPawn && isDiagonal && targetWasEmpty) {
      const captureY = piece === 'P' ? to.y + 1 : to.y - 1;
      if (captureY >= 0 && captureY <= 7) {
        board[captureY][to.x] = null;
      }
    }

    let placedPiece = piece;
    const isWhitePawnPromotion = piece === 'P' && to.y === 0;
    const isBlackPawnPromotion = piece === 'p' && to.y === 7;
    if (isWhitePawnPromotion || isBlackPawnPromotion) {
      const rawPromotion = String(promotion || '').trim().toLowerCase();
      const normalizedPromotion = ['q', 'r', 'b', 'n'].includes(rawPromotion) ? rawPromotion : 'q';
      placedPiece = isWhitePawnPromotion
        ? normalizedPromotion.toUpperCase()
        : normalizedPromotion.toLowerCase();
    }

    board[to.y][to.x] = placedPiece;

    // Handle castling in placement mode: when king moves 2 squares horizontally,
    // also move the rook from the corner to the square next to king.
    const isKing = piece === 'K' || piece === 'k';
    const sameRank = from.y === to.y;
    const twoFiles = Math.abs(to.x - from.x) === 2;
    if (isKing && sameRank && twoFiles) {
      const isKingSide = to.x > from.x;
      const rookFromX = isKingSide ? 7 : 0;
      const rookToX = isKingSide ? (to.x - 1) : (to.x + 1);
      const rookPiece = piece === 'K' ? 'R' : 'r';
      if (board[from.y][rookFromX] === rookPiece) {
        board[from.y][rookFromX] = null;
        board[from.y][rookToX] = rookPiece;
      }
    }

    const encodeRank = (cells) => {
      let out = '';
      let empties = 0;
      for (const cell of cells) {
        if (!cell) {
          empties += 1;
        } else {
          if (empties) out += String(empties);
          empties = 0;
          out += cell;
        }
      }
      if (empties) out += String(empties);
      return out;
    };

    return board.map(encodeRank).join('/');
  };

  // Board controller for guided steps: keep wrong attempts visible, then allow manual reset.
  const resetBoardToStep = () => {
    setPositionOverride(null);
    setMoveFeedback(null);
    setHasAttemptedMove(false);
    setPendingConfirm(null);
    setTransientSquareStyles({});
    setSelectionSquareStyles({});
    setSelectedMoveSource('');
    setSolutionVisible(false);
    setSolutionUnlocked(false);
    setSolutionMoveIndex(0);
    setSolutionPlaybackPlacement(null);
    setGuidedMoveIndex(0);
    // Restore step annotations after feedback is cleared.
    // loadAnnotations effect will re-run when moveFeedback changes.
  };

  useEffect(() => {
    // Step change: reset interactive state and feedback.
    setPositionOverride(null);
    setMoveFeedback(null);
    setHasAttemptedMove(false);
    setActionFeedback(null);
    setActionCompleted(false);
    setFocusModeOn(false);
    setAdjustArmed(false);
    setPendingConfirm(null);
    setTransientSquareStyles({});
    setSparkRainOn(false);
    setSelectionSquareStyles({});
    setSelectedMoveSource('');
    setSolutionVisible(false);
    setSolutionUnlocked(false);
    setSolutionMoveIndex(0);
    setSolutionPlaybackPlacement(null);
    setPuzzleWrongStreak(0);
    setPuzzleOutcomeSent(false);
    setPuzzleScoreSubmitted(false);
    setPuzzleAwardResult(null);
    setSegmentStartedAt(Date.now());
    setGuidedMoveIndex(0);
    setCctSelectedIds([]);
    setCctSubmitted(false);
  }, [currentSegment]);

  useEffect(() => {
    // Extra guard: if step FEN changes, always drop previous board override.
    // Prevents showing previous-step board with current-step text.
    setPositionOverride(null);
    setSelectionSquareStyles({});
    setSelectedMoveSource('');
    setTransientSquareStyles({});
    setGuidedMoveIndex(0);
  }, [segment?.fen, segment?.stepId]);

  useEffect(() => {
    let alive = true;

    async function loadAnnotations() {
      const segs = lessonData?.segments || [];
      const seg = segs[currentSegment];
      if (!seg?.stepId) {
        setBoardArrows([]);
        setSquareStyles({});
        setBaseStepAnnotations({
          arrows: [],
          arrowColor: 'rgb(255,170,0)',
          squareStyles: {}
        });
        return;
      }

      try {
        let data = await fetchStepAnnotations(seg.stepId);
        // Dev-friendly fallback: if steps were reseeded while the lesson is open,
        // old step ids can return zero annotations. Resolve step id by (lesson_id, step_order).
        if (
          lessonData?.lessonDbId &&
          seg?.id &&
          data &&
          Array.isArray(data.customArrows) &&
          Object.keys(data.customSquareStyles || {}).length === 0 &&
          data.customArrows.length === 0
        ) {
          const fallback = await fetchStepAnnotationsByLessonAndOrder(lessonData.lessonDbId, seg.id);
          if (fallback) data = fallback;
        }
        if (!alive || !data) return;
        const nextArrows = data.customArrows || [];
        const nextArrowColor = data.customArrowColor || 'rgb(255,170,0)';
        const nextSquareStyles = data.customSquareStyles || {};
        const hidePuzzleHintsByDefault = String(lessonData?.lessonType || '').toLowerCase() === 'puzzle';
        setBaseStepAnnotations({
          arrows: nextArrows,
          arrowColor: nextArrowColor,
          squareStyles: nextSquareStyles
        });
        if (hidePuzzleHintsByDefault && !solutionVisible) {
          setBoardArrows([]);
          setArrowColor('rgb(255,170,0)');
          setSquareStyles({});
        } else {
          setBoardArrows(nextArrows);
          setArrowColor(nextArrowColor);
          setSquareStyles(nextSquareStyles);
        }
      } catch {
        if (!alive) return;
        setBoardArrows([]);
        setSquareStyles({});
        setBaseStepAnnotations({
          arrows: [],
          arrowColor: 'rgb(255,170,0)',
          squareStyles: {}
        });
      }
    }

    loadAnnotations();
    return () => {
      alive = false;
    };
  }, [lessonData, currentSegment, solutionVisible]);

  useEffect(() => {
    if (!ttsEnabled) return;
    if (!segment?.text) return;

    // Slight delay so the UI feels synced with the step change.
    const t = window.setTimeout(() => {
      playNarration({ text: cleanLessonText(segment.text), audioUrl: segment.audioUrl });
    }, 220);
    return () => window.clearTimeout(t);
  }, [ttsEnabled, segment?.text, segment?.audioUrl, playNarration]);

  const speakFeedback = (text) => {
    // Feedback voice is intentionally disabled to avoid overlap with DB audio.
    return;
  };

  useEffect(() => {
    // If language changes while in-lesson, stop current speech and let the reload re-trigger.
    stopTTS();
    stopNarrationAudio();
  }, [language, stopNarrationAudio]);

  useEffect(() => {
    if (ttsEnabled) return;
    stopTTS();
    stopNarrationAudio();
  }, [ttsEnabled, stopNarrationAudio]);

  useEffect(() => {
    return () => {
      stopTTS();
      stopNarrationAudio();
    };
  }, [stopNarrationAudio]);

  const saveProgressToStep = async ({ nextStepIndex1Based, completed }) => {
    if (!trackProgress) return;
    if (!lessonData?.lessonDbId) return;
    if (!user?.id) return;
    const segs = lessonData?.segments || [];
    if (!segs.length) return;

    const step = Math.min(segs.length, Math.max(1, Number(nextStepIndex1Based || 1)));
    try {
      await upsertLessonProgress({
        lessonDbId: lessonData.lessonDbId,
        currentStep: step,
        completed: Boolean(completed)
      });
      await upsertLearningPathState({
        currentModuleId: lessonData?.moduleDbId || null,
        currentLessonId: lessonData?.lessonDbId || null
      });
    } catch (e) {
      // Debug signal: if this fails, progress won't update in AcademyHome.
      // Common causes: missing RLS policies or expired session.
      // eslint-disable-next-line no-console
      console.warn('[LessonViewer] Failed to save progress', {
        lessonId: lessonData.lessonDbId,
        step,
        completed: Boolean(completed),
        error: String(e?.message || e)
      });
    }
  };

  const handleNext = () => {
    if (currentSegment < segments.length - 1) {
      // Save progress when completing this step (moving to the next).
      // currentStep is 1-based "where you are"; completing step N means next saved step is N+1.
      saveProgressToStep({ nextStepIndex1Based: currentSegment + 2, completed: false });
      setCurrentSegment(currentSegment + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSegment > 0) {
      setCurrentSegment(currentSegment - 1);
    }
  };

  const handleDone = async () => {
    if (!segments.length) return;
    // Persist completion so AcademyHome can show "Done" immediately.
    if (trackProgress) {
      await saveProgressToStep({ nextStepIndex1Based: segments.length, completed: true });
    }
    const lessonType = String(lessonData?.lessonType || '').trim().toLowerCase();
    const isAssessmentLesson =
      lessonType === 'recap' || lessonType === 'exam_standard' || lessonType === 'exam_captain';
    if (trackProgress && isAssessmentLesson) {
      const passed = hasLessonQuiz ? Boolean(quizPassed) : true;
      const score = hasLessonQuiz ? Number(quizScorePct) : null;
      try {
        await insertAssessmentResult({
          lessonId: lessonData?.lessonDbId || null,
          assessmentKind: lessonType,
          score,
          passed
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[LessonViewer] Failed to save assessment result', {
          lessonId: lessonData?.lessonDbId,
          assessmentKind: lessonType,
          score,
          passed,
          error: String(e?.message || e)
        });
      }
    }
    if (typeof onComplete === 'function') {
      const resolvedPuzzleResult = isPuzzleFlow
        ? (solutionVisible ? 'success_hint' : 'success')
        : '';
      onComplete({
        lessonSlug: lessonData?.id || null,
        lessonDbId: lessonData?.lessonDbId || null,
        lessonTitle: lessonData?.title || null,
        puzzleCatalogId: lessonData?.puzzleCatalogId || null,
        puzzleResult: resolvedPuzzleResult,
        puzzleScoredAlready: Boolean(puzzleScoreSubmitted),
        puzzleAward: puzzleAwardResult || null,
        playedMoveUci: lastCorrectMoveUci || '',
        tries: puzzleTries || 1,
        usedSolution: Boolean(solutionVisible),
        solveMs: Math.max(0, Date.now() - Number(segmentStartedAt || Date.now()))
      });
      return;
    }
    onClose();
  };

  const progress = segments.length ? ((currentSegment + 1) / segments.length) * 100 : 0;

  const rawExpectedMove = useMemo(() => (
    interaction?.expected ??
    interaction?.expected_move ??
    interaction?.expectedMove ??
    (interaction?.expected_from && interaction?.expected_to
      ? {
          from: interaction.expected_from,
          to: interaction.expected_to,
          promotion: interaction?.promotion
        }
      : null) ??
    (interaction?.from && interaction?.to
      ? {
          from: interaction.from,
          to: interaction.to,
          promotion: interaction?.promotion
        }
      : null)
  ), [interaction]);
  const parseUciMove = useCallback((value) => {
    const token = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[+#]/g, '')
      .replace('=', '')
      .replace(/\s+/g, '')
      .replace(/[→>-]/g, '');
    const match = token.match(/^([a-h][1-8])([a-h][1-8])([qrbn])?$/);
    if (!match) return null;
    return { from: match[1], to: match[2], promotion: match[3] || undefined };
  }, []);
  const parseMoveObject = useCallback((m) => {
    if (!m) return null;
    if (typeof m === 'string') return parseUciMove(m);
    const from = String(m?.from || '').toLowerCase();
    const to = String(m?.to || '').toLowerCase();
    if (from.length !== 2 || to.length !== 2) return null;
    const rawPromotion = String(m?.promotion || '').trim().toLowerCase();
    const promotion = ['q', 'r', 'b', 'n'].includes(rawPromotion) ? rawPromotion : undefined;
    return { from, to, promotion };
  }, [parseUciMove]);
  const expectedFallbackMove = useMemo(() => parseMoveObject(rawExpectedMove), [rawExpectedMove, parseMoveObject]);
  const hasExpectedFallbackMove = Boolean(expectedFallbackMove?.from) && Boolean(expectedFallbackMove?.to);
  const hasAnyGuidedMoveData =
    (typeof interaction?.lineUci === 'string' && Boolean(interaction.lineUci.trim())) ||
    (Array.isArray(interaction?.lineUci) && interaction.lineUci.length > 0) ||
    (Array.isArray(interaction?.solutionMoves) && interaction.solutionMoves.length > 0) ||
    (Array.isArray(interaction?.guidedMoves) && interaction.guidedMoves.length > 0) ||
    hasExpectedFallbackMove;
  const isMoveInteraction = isStepEngineV2
    ? stepEngine.isMoveInteraction
    : (interaction?.type === 'move' || hasAnyGuidedMoveData);
  const isCctInteraction = interaction?.type === 'cct_check';
  const isPuzzleFlow = String(lessonData?.lessonType || '').toLowerCase() === 'puzzle';
  const puzzleMeta = lessonData?.puzzleMeta || null;
  const puzzleHeaderStats = lessonData?.puzzleHeaderStats || null;
  const puzzleLastOutcome = lessonData?.puzzleLastOutcome || null;
  const puzzleRating = Number(puzzleMeta?.rating || 0);
  const puzzleDifficulty = useMemo(() => {
    if (puzzleRating >= 2000) return language === 'ro' ? 'Expert' : 'Expert';
    if (puzzleRating >= 1600) return language === 'ro' ? 'Greu' : 'Hard';
    if (puzzleRating >= 1300) return language === 'ro' ? 'Avansat' : 'Advanced';
    if (puzzleRating >= 1000) return language === 'ro' ? 'Intermediar' : 'Intermediate';
    return language === 'ro' ? 'Începător' : 'Beginner';
  }, [puzzleRating, language]);
  const puzzleDifficultyIcon = useMemo(() => {
    if (puzzleRating >= 2000) return '♛';
    if (puzzleRating >= 1600) return '♜';
    if (puzzleRating >= 1300) return '♝';
    if (puzzleRating >= 1000) return '♞';
    return '♟';
  }, [puzzleRating]);
  const puzzleDifficultyClass = useMemo(() => {
    if (puzzleRating >= 2000) return 'puzzle-badge-expert';
    if (puzzleRating >= 1600) return 'puzzle-badge-hard';
    if (puzzleRating >= 1300) return 'puzzle-badge-hard';
    if (puzzleRating >= 1000) return 'puzzle-badge-medium';
    return 'puzzle-badge-easy';
  }, [puzzleRating]);
  const puzzleRatingClass = useMemo(() => {
    if (puzzleRating >= 2000) return 'puzzle-rating-expert';
    if (puzzleRating >= 1600) return 'puzzle-rating-hard';
    if (puzzleRating >= 1300) return 'puzzle-rating-hard';
    if (puzzleRating >= 1000) return 'puzzle-rating-medium';
    return 'puzzle-rating-easy';
  }, [puzzleRating]);
  const puzzleHeaderRating = Math.round(
    Number(puzzleHeaderStats?.puzzleRating || 500)
  );
  const puzzleHeaderPoints = Math.max(
    0,
    Number(puzzleHeaderStats?.puzzlePoints || 0)
  );
  const puzzlePrimaryTheme = useMemo(() => {
    const list = Array.isArray(puzzleMeta?.themes) ? puzzleMeta.themes : [];
    const explicitTheme = String(lessonData?.puzzleFilter?.themeId || '').trim().toLowerCase();
    const normalizedList = list.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean);
    const preferredOrder = ['matein1', 'matein2', 'fork', 'pin', 'skewer', 'discoveredattack', 'doublecheck'];
    const prioritizedFromList =
      preferredOrder.find((t) => normalizedList.includes(t)) ||
      normalizedList[0] ||
      '';
    const raw = explicitTheme || prioritizedFromList;
    if (!raw) return '';
    const normalized = raw.toLowerCase();
    if (normalized === 'matein1') return language === 'ro' ? 'Mat în 1' : 'Mate in 1';
    if (normalized === 'matein2') return language === 'ro' ? 'Mat în 2' : 'Mate in 2';
    if (normalized === 'fork') return language === 'ro' ? 'Furculiță' : 'Fork';
    if (normalized === 'pin') return language === 'ro' ? 'Țintuire' : 'Pin';
    if (normalized === 'skewer') return language === 'ro' ? 'Atac înțepat (Skewer)' : 'Skewer';
    if (normalized === 'discoveredattack') return language === 'ro' ? 'Atac descoperit' : 'Discovered attack';
    if (normalized === 'doublecheck') return language === 'ro' ? 'Dublu șah' : 'Double check';
    const pretty = normalized
      .replace(/([a-z])([0-9])/g, '$1 $2')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return pretty || raw;
  }, [puzzleMeta?.themes, lessonData?.puzzleFilter?.themeId, language]);
  const puzzleHeaderIq = Math.max(
    0,
    Number(puzzleHeaderStats?.iqPoints || 0)
  );
  const puzzleHeaderProfile = String(
    puzzleHeaderStats?.profileLabel ||
      (language === 'ro' ? 'Profil: Începător' : 'Profile: Beginner')
  );
  const puzzleHeaderRank = Number.isFinite(Number(puzzleHeaderStats?.globalRank))
    ? Number(puzzleHeaderStats.globalRank)
    : null;
  const puzzleOutcomeUi = useMemo(() => {
    if (!isPuzzleFlow || !puzzleLastOutcome) return null;
    const key = String(puzzleLastOutcome?.result || '').toLowerCase();
    const ratingDelta = Number(puzzleLastOutcome?.ratingDelta || 0);
    const pointsDelta = Number(puzzleLastOutcome?.pointsDelta || 0);
    const signed = (n) => (n > 0 ? `+${n}` : `${n}`);
    const byKey = {
      success: {
        cls: 'puzzle-outcome-success',
        title: language === 'ro' ? 'Ai rezolvat cu succes' : 'Solved successfully'
      },
      success_hint: {
        cls: 'puzzle-outcome-hint',
        title: language === 'ro' ? 'Rezolvat cu ajutor' : 'Solved with hint'
      },
      already_solved: {
        cls: 'puzzle-outcome-neutral',
        title: language === 'ro' ? 'Deja rezolvat (fără puncte bonus)' : 'Already solved (no bonus points)'
      },
      fail3: {
        cls: 'puzzle-outcome-fail',
        title: language === 'ro' ? 'Ai ratat după 3 încercări' : 'Failed after 3 tries'
      },
      abandon: {
        cls: 'puzzle-outcome-abandon',
        title: language === 'ro' ? 'Puzzle abandonat' : 'Puzzle abandoned'
      }
    };
    const base = byKey[key] || {
      cls: 'puzzle-outcome-neutral',
      title: language === 'ro' ? `Rezultat: ${key || '-'}` : `Result: ${key || '-'}`
    };
    return {
      ...base,
      detailsText:
        language === 'ro'
          ? `Ai câștigat Rating ${signed(ratingDelta)} și IQ Points ${signed(pointsDelta)}`
          : `You earned Rating ${signed(ratingDelta)} and IQ Points ${signed(pointsDelta)}`
    };
  }, [isPuzzleFlow, puzzleLastOutcome, language]);
  const requiresCorrectMove =
    isMoveInteraction && interaction?.lockNextUntilCorrect;
  const requiresMoveAttempt =
    isMoveInteraction && interaction?.requireAttempt !== false;
  const requiresAction =
    (interaction?.type === 'action' && interaction?.lockNextUntilDone) ||
    (isCctInteraction && interaction?.lockNextUntilDone !== false);

  const isMoveCorrect = moveFeedback?.status === 'correct';
  const legacyGuidedMoves = useMemo(() => {
    if (!isMoveInteraction) return [];
    if (Array.isArray(interaction?.lineUci) && interaction.lineUci.length) {
      return interaction.lineUci.map(parseMoveObject).filter(Boolean);
    }
    if (typeof interaction?.lineUci === 'string' && interaction.lineUci.trim()) {
      return interaction.lineUci
        .split(/\s+/)
        .map(parseUciMove)
        .filter(Boolean);
    }
    if (Array.isArray(interaction?.solutionMoves) && interaction.solutionMoves.length) {
      return interaction.solutionMoves.map(parseMoveObject).filter(Boolean);
    }
    if (Array.isArray(interaction?.guidedMoves) && interaction.guidedMoves.length) {
      return interaction.guidedMoves.map(parseMoveObject).filter(Boolean);
    }
    if (expectedFallbackMove) {
      return [expectedFallbackMove];
    }
    return [];
  }, [interaction, isMoveInteraction, parseMoveObject, parseUciMove, expectedFallbackMove]);
  const guidedMoves = isStepEngineV2 ? stepEngine.guidedMoves : legacyGuidedMoves;
  const expectedMove = guidedMoves[guidedMoveIndex] || null;
  const hasExpectedMove = Boolean(expectedMove?.from) && Boolean(expectedMove?.to);
  const solutionMoves = guidedMoves;
  const hasNextSolutionMove = solutionVisible && solutionMoveIndex < Math.max(0, solutionMoves.length - 1);
  const canApplyCurrentSolutionMove = solutionVisible && solutionMoveIndex < solutionMoves.length;
  const isLastSegment = segments.length > 0 && currentSegment === segments.length - 1;
  const lessonQuiz = lessonData?.quiz || null;
  const quizQuestions = Array.isArray(lessonQuiz?.questions) ? lessonQuiz.questions : [];
  const hasLessonQuiz = Boolean(quizQuestions.length > 0);
  const currentQuizQuestion = hasLessonQuiz ? quizQuestions[Math.min(quizIndex, quizQuestions.length - 1)] : null;
  const quizScorePct = hasLessonQuiz ? Math.round((quizCorrectCount / quizQuestions.length) * 100) : 0;
  const configuredQuizPassScore = Number(lessonQuiz?.passScore || 0);
  // Platform rule: lesson quizzes require at least 75% to allow completion.
  const quizPassScore = Math.max(75, configuredQuizPassScore);
  const quizPassed = quizCompleted ? quizScorePct >= quizPassScore : false;
  const requiresMoveSequence = isMoveInteraction && requiresCorrectMove && guidedMoves.length > 0;
  const hasResolvableExpectedMove = useMemo(() => {
    if (!requiresMoveSequence) return false;
    if (!expectedMove?.from || !expectedMove?.to) return false;
    const pieceAtSource = getPieceAt(boardPosition, expectedMove.from);
    if (!pieceAtSource) return false;
    return true;
  }, [requiresMoveSequence, expectedMove, boardPosition]);
  const moveSequenceComplete =
    !requiresMoveSequence ||
    guidedMoveIndex >= guidedMoves.length ||
    !hasResolvableExpectedMove;
  const puzzleCanContinue =
    isPuzzleFlow &&
    (
      (isMoveCorrect && moveSequenceComplete) ||
      solutionUnlocked ||
      Boolean(puzzleAwardResult) ||
      Boolean(puzzleOutcomeUi) ||
      puzzleScoreSubmitted ||
      puzzleOutcomeSent
    );
  const nextDisabled =
    !segments.length ||
    (requiresMoveAttempt && !hasAttemptedMove) ||
    (requiresCorrectMove && !moveSequenceComplete) ||
    (requiresAction && !actionCompleted);
  const doneDisabled = isPuzzleFlow
    ? (!segments.length || !puzzleCanContinue)
    : (
      !segments.length ||
      (isLastSegment && hasLessonQuiz && !quizPassed) ||
      (
        // If lesson quiz is passed on the last step, allow finishing even if
        // the step had an interaction gate. Quiz pass is treated as final gate.
        (isLastSegment && hasLessonQuiz && quizPassed)
          ? false
          : (
            (isMoveInteraction ? (requiresCorrectMove ? !(isMoveCorrect || solutionUnlocked) : !hasAttemptedMove) : false) ||
            (isMoveInteraction ? (requiresCorrectMove ? !moveSequenceComplete : false) : false) ||
            ((interaction?.type === 'action' || isCctInteraction) ? (requiresAction ? !actionCompleted : false) : false)
          )
      )
    );

  const feedbackColors = useMemo(() => {
    if (!moveFeedback) return null;
    if (moveFeedback.status === 'correct') return { border: '#16a34a', bg: '#ecfdf5', text: '#14532d' };
    return { border: '#dc2626', bg: '#fef2f2', text: '#7f1d1d' };
  }, [moveFeedback]);

  const isGuidedMoveStep = isMoveInteraction;
  const currentFenForMoveRules = useMemo(() => {
    if (!segment?.fen) return '';
    const parts = String(segment.fen).trim().split(/\s+/);
    const placement = boardPosition === 'start' ? parts[0] : boardPosition;
    const turn = parts[1] || 'w';
    const castling = parts[2] || '-';
    const enPassant = parts[3] || '-';
    const half = parts[4] || '0';
    const full = parts[5] || '1';
    return `${placement} ${turn} ${castling} ${enPassant} ${half} ${full}`;
  }, [segment?.fen, boardPosition]);
  const stepTitleText = useMemo(() => {
    const raw = interaction?.stepTitle;
    if (raw && typeof raw === 'object') {
      const v = raw[language];
      if (typeof v === 'string' && v.trim()) return cleanLessonText(v);
    }
    const legacy = language === 'ro' ? interaction?.step_title_ro : interaction?.step_title_en;
    if (typeof legacy === 'string' && legacy.trim()) return cleanLessonText(legacy);
    return '';
  }, [interaction, language]);
  const missionText = useMemo(() => {
    if (!interaction) return null;
    if (interaction?.type !== 'move' && interaction?.type !== 'action' && interaction?.type !== 'cct_check') return null;
    const promptObj = interaction?.prompt;
    if (promptObj && typeof promptObj === 'object') {
      const v = promptObj[language];
      if (typeof v === 'string' && v.trim()) return cleanLessonText(v);
    }
    const legacy =
      language === 'ro'
        ? interaction?.prompt_ro || interaction?.mission_ro
        : interaction?.prompt_en || interaction?.mission_en;
    if (typeof legacy === 'string' && legacy.trim()) return cleanLessonText(legacy);
    return null;
  }, [interaction, language]);
  const cctOptions = useMemo(() => {
    if (!isCctInteraction) return [];
    const source = Array.isArray(interaction?.cctOptions)
      ? interaction.cctOptions
      : Array.isArray(interaction?.options)
        ? interaction.options
        : [];
    return source
      .map((opt) => {
        if (!opt || typeof opt !== 'object') return null;
        const id = String(opt.id || '').trim();
        if (!id) return null;
        const text =
          cleanLessonText(
            (typeof opt?.text === 'object'
              ? (opt.text?.[language] || opt.text?.ro || opt.text?.en || '')
              : ((language === 'ro' ? opt?.text_ro : opt?.text_en) || opt?.text || '')) || ''
          ) || id;
        return { id, text, isCorrect: Boolean(opt?.isCorrect) };
      })
      .filter(Boolean);
  }, [interaction, isCctInteraction, language]);
  const cctCorrectIds = useMemo(() => {
    if (!isCctInteraction) return [];
    if (Array.isArray(interaction?.correctOptionIds)) {
      return interaction.correctOptionIds.map((x) => String(x || '').trim()).filter(Boolean);
    }
    return cctOptions.filter((x) => x.isCorrect).map((x) => x.id);
  }, [interaction, isCctInteraction, cctOptions]);
  const structuredMoveFeedback = useMemo(() => {
    if (!moveFeedback || moveFeedback.status !== 'correct') return null;
    const container = interaction?.feedback;
    if (!container || typeof container !== 'object') return null;
    const localized =
      (typeof container?.[language] === 'object' && container[language]) ||
      (typeof container?.ro === 'object' && container.ro) ||
      (typeof container?.en === 'object' && container.en) ||
      container;
    const normalizeFeedbackText = (value) => {
      const primary = resolveLocalizedText(value, language);
      if (primary) return primary;
      if (value && typeof value === 'object') {
        const fallback =
          resolveLocalizedText(value?.ro || value?.en || value?.text || value?.label || '', language);
        return fallback || '';
      }
      return cleanLessonText(value ?? '');
    };
    const why = normalizeFeedbackText(localized?.why_good || localized?.why || '');
    const threat = normalizeFeedbackText(localized?.threat_created || localized?.threat || '');
    const block = normalizeFeedbackText(localized?.piece_restricted || localized?.block || '');
    const next = normalizeFeedbackText(localized?.next_plan || localized?.plan || '');
    if (!why && !threat && !block && !next) return null;
    return { why, threat, block, next };
  }, [moveFeedback, interaction, language]);
  const stepNumberLabel = useMemo(() => {
    if (isPuzzleFlow) {
      return language === 'ro'
        ? `Diagrama #${Math.max(1, Number(lessonData?.puzzleDiagramNo || 1))}`
        : `Diagram #${Math.max(1, Number(lessonData?.puzzleDiagramNo || 1))}`;
    }

    const base = `${language === 'ro' ? 'Pas' : 'Step'} ${currentSegment + 1}`;
    const rawTitle = String(stepTitleText || '').trim();
    if (!rawTitle) return base;

    // Avoid duplicate prefixes like "Pas 4 · Pas 4 · Regula de Aur"
    const genericPrefix = /^(pas|step)\s*\d+\b/i;
    if (genericPrefix.test(rawTitle)) {
      return rawTitle;
    }
    return base;
  }, [isPuzzleFlow, language, lessonData?.puzzleDiagramNo, currentSegment, stepTitleText]);
  const shouldAppendStepTitle = useMemo(() => {
    if (isPuzzleFlow) return false;
    const rawTitle = String(stepTitleText || '').trim();
    if (!rawTitle) return false;
    return !/^(pas|step)\s*\d+\b/i.test(rawTitle);
  }, [isPuzzleFlow, stepTitleText]);

  const awardPuzzleNow = useCallback(async ({
    result,
    playedMoveUci = '',
    tries,
    usedSolution
  }) => {
    if (!isPuzzleFlow) return null;
    if (!lessonData?.puzzleCatalogId) return null;
    if (!user?.id) return null;
    if (puzzleScoreSubmitted) return puzzleAwardResult || null;
    if (typeof onPuzzleScored !== 'function') return null;

    setPuzzleScoreSubmitted(true);
    try {
      const award = await onPuzzleScored({
        lessonSlug: lessonData?.id || null,
        lessonDbId: lessonData?.lessonDbId || null,
        lessonTitle: lessonData?.title || null,
        puzzleCatalogId: lessonData?.puzzleCatalogId || null,
        puzzleResult: String(result || '').trim().toLowerCase(),
        playedMoveUci: playedMoveUci || '',
        tries: Number(tries || 1),
        usedSolution: Boolean(usedSolution),
        solveMs: Math.max(0, Date.now() - Number(segmentStartedAt || Date.now()))
      });
      if (award) {
        setPuzzleAwardResult(award);
      }
      return award || null;
    } catch {
      return null;
    }
  }, [
    isPuzzleFlow,
    lessonData,
    user?.id,
    puzzleScoreSubmitted,
    puzzleAwardResult,
    onPuzzleScored,
    segmentStartedAt
  ]);

  const setOverlayForExpected = (status) => {
    if (!expectedMove?.from || !expectedMove?.to) return;
    if (isPuzzleFlow && !solutionVisible) return;
    setBoardArrows([[expectedMove.from, expectedMove.to]]);
    setArrowColor(status === 'correct' ? '#22c55e' : '#ef4444');
  };

  const canShowBoard = Boolean(segment?.fen) && !isLoading && !error;
  const isLockedByWrongAttempt = !isPuzzleFlow && moveFeedback?.status === 'wrong';
  const debugEnabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === '1';

  const confirmText = useMemo(() => {
    if (!interaction || !isMoveInteraction) return '';
    const c = interaction?.confirmCapture;
    if (!c) return '';
    const t = interaction?.confirmText;
    if (t && typeof t === 'object') {
      const v = t[language];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return language === 'ro' ? 'Ești sigur că vrei să capturezi?' : 'Are you sure you want to capture?';
  }, [interaction, isMoveInteraction, language]);

  const pulseRootAt = (square) => {
    if (!square) return;
    const sq = String(square).trim();
    setTransientSquareStyles({
      [sq]: {
        boxShadow: 'inset 0 0 0 3px rgba(34,197,94,0.85)',
        background: 'radial-gradient(circle at 50% 55%, rgba(34,197,94,0.25) 0%, rgba(34,197,94,0.05) 60%, rgba(34,197,94,0) 75%)',
        animation: 'cmRootPulse 650ms ease-out both'
      }
    });
    window.setTimeout(() => setTransientSquareStyles({}), 680);
  };

  const applyCorrectGuidedMove = ({
    sourceSquare,
    targetSquare,
    promotion,
    sequenceDone,
    resultPlacement
  }) => {
    playMoveSound();
    playSuccessSound();
    const nextPlacement = resultPlacement || moveInPlacement(boardPosition, sourceSquare, targetSquare, promotion);
    setPositionOverride(nextPlacement);
    pulseRootAt(targetSquare);
    const msg = sequenceDone
      ? (
        language === 'ro'
          ? 'Exact! O mutare excelentă, continuă tot așa!'
          : "Exactly! That's an excellent move, keep it up!"
      )
      : (
        language === 'ro'
          ? 'Corect! Continuă linia: joacă și următoarea mutare din secvență.'
          : 'Correct! Continue the line: play the next move in the sequence.'
      );
    setMoveFeedback({ status: 'correct', message: msg });
    setPuzzleWrongStreak(0);
    setLastCorrectMoveUci(`${String(sourceSquare || '').toLowerCase()}${String(targetSquare || '').toLowerCase()}`);
    if (sequenceDone) {
      setOverlayForExpected('correct');
    }
    setBurstTrigger((x) => x + 1);
    speakFeedback(msg);
    if (isPuzzleFlow && sequenceDone) {
      void awardPuzzleNow({
        result: solutionVisible ? 'success_hint' : 'success',
        playedMoveUci: `${String(sourceSquare || '').toLowerCase()}${String(targetSquare || '').toLowerCase()}`,
        tries: puzzleTries || 1,
        usedSolution: Boolean(solutionVisible)
      });
    }

    // Save progress immediately on a correct step, so Academy shows progress even before Next.
    if (sequenceDone) {
      const segCount = segments.length || 0;
      const isLast = segCount > 0 && currentSegment === segCount - 1;
      if (isLast) {
        saveProgressToStep({ nextStepIndex1Based: segCount, completed: true });
      } else {
        saveProgressToStep({ nextStepIndex1Based: currentSegment + 2, completed: false });
      }
    }

    if (sequenceDone && interaction?.autoAdvanceMs) {
      window.setTimeout(() => {
        setMoveFeedback(null);
        handleNext();
      }, Number(interaction.autoAdvanceMs) || 900);
    }
  };

  const showSolutionMoveHint = useCallback((index, playbackPlacement) => {
    const move = solutionMoves[index];
    if (!move?.from || !move?.to) return;
    setBoardArrows([[move.from, move.to]]);
    setArrowColor('rgb(37,99,235)');
    setSelectionSquareStyles({
      [move.from]: {
        boxShadow: 'inset 0 0 0 3px rgba(37,99,235,0.9)',
        background: 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.24) 0%, rgba(59,130,246,0.06) 60%, rgba(59,130,246,0) 76%)'
      },
      [move.to]: {
        boxShadow: 'inset 0 0 0 3px rgba(16,185,129,0.9)',
        background: 'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0.12) 48%, rgba(16,185,129,0) 76%)'
      }
    });
    setPositionOverride(playbackPlacement || baseBoardPosition);
  }, [solutionMoves, baseBoardPosition]);

  const handleRevealSolution = useCallback(() => {
    if (!solutionMoves.length) return;
    const startPlacement = baseBoardPosition;
    setPositionOverride(startPlacement);
    setSolutionPlaybackPlacement(startPlacement);
    setSolutionMoveIndex(0);
    setSolutionVisible(true);
    setSolutionUnlocked(true);
    setHasAttemptedMove(true);
    setPendingConfirm(null);
    setTransientSquareStyles({});
    showSolutionMoveHint(0, startPlacement);
    const msg =
      language === 'ro'
        ? 'Soluția este afișată. Urmărește săgeata și aplică mutarea recomandată.'
        : 'Solution is shown. Follow the arrow and apply the recommended move.';
    setMoveFeedback({ status: 'correct', message: msg });
    if (isPuzzleFlow) {
      void awardPuzzleNow({
        result: 'success_hint',
        playedMoveUci: '',
        tries: puzzleTries || 1,
        usedSolution: true
      });
    }
  }, [
    solutionMoves.length,
    baseBoardPosition,
    showSolutionMoveHint,
    language,
    isPuzzleFlow,
    awardPuzzleNow,
    puzzleTries
  ]);

  const handleSelectQuizAnswer = useCallback((optionId) => {
    if (!currentQuizQuestion || quizChecked) return;
    const picked = String(optionId || '');
    if (!picked) return;

    setQuizSelectedOptionId(picked);
    setQuizChecked(true);
    const qid = String(currentQuizQuestion.id || `q-${quizIndex}`);
    const alreadyAnswered = Boolean(quizAnsweredIds[qid]);
    if (!alreadyAnswered && picked === String(currentQuizQuestion.correctOptionId || '')) {
      setQuizCorrectCount((prev) => prev + 1);
    }
    if (!alreadyAnswered) {
      setQuizAnsweredIds((prev) => ({ ...prev, [qid]: true }));
    }
  }, [currentQuizQuestion, quizChecked, quizIndex, quizAnsweredIds]);

  const handleNextQuizQuestion = useCallback(() => {
    if (!hasLessonQuiz) return;
    if (quizIndex >= quizQuestions.length - 1) {
      setQuizCompleted(true);
      return;
    }
    setQuizIndex((prev) => prev + 1);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
  }, [hasLessonQuiz, quizIndex, quizQuestions.length]);

  const handleRetryQuiz = useCallback(() => {
    if (!hasLessonQuiz) return;
    setQuizStarted(true);
    setQuizIndex(0);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
    setQuizCorrectCount(0);
    setQuizAnsweredIds({});
    setQuizCompleted(false);
  }, [hasLessonQuiz]);

  const handleApplySolutionMove = useCallback(() => {
    if (!solutionVisible) return;
    const move = solutionMoves[solutionMoveIndex];
    if (!move?.from || !move?.to) return;

    const placementBefore = solutionPlaybackPlacement || baseBoardPosition;
    const placementAfter = moveInPlacement(placementBefore, move.from, move.to, move.promotion);
    setPositionOverride(placementAfter);
    setSolutionPlaybackPlacement(placementAfter);

    if (solutionMoveIndex < solutionMoves.length - 1) {
      const nextIndex = solutionMoveIndex + 1;
      setSolutionMoveIndex(nextIndex);
      showSolutionMoveHint(nextIndex, placementAfter);
      const msg =
        language === 'ro'
          ? `Mutare aplicată (${move.from} → ${move.to}). Continuă cu următoarea mutare din linia câștigătoare.`
          : `Move applied (${move.from} → ${move.to}). Continue with the next move from the winning line.`;
      setMoveFeedback({ status: 'correct', message: msg });
      return;
    }

    setBoardArrows([]);
    setSelectionSquareStyles({});
    const doneMsg =
      language === 'ro'
        ? `Linia soluției este completă (${move.from} → ${move.to}). Poți continua antrenamentul.`
        : `Solution line completed (${move.from} → ${move.to}). You can continue training.`;
    setMoveFeedback({ status: 'correct', message: doneMsg });
  }, [
    solutionVisible,
    solutionMoves,
    solutionMoveIndex,
    solutionPlaybackPlacement,
    baseBoardPosition,
    showSolutionMoveHint,
    language
  ]);

  const handleGuidedMoveAttempt = (sourceSquare, targetSquare) => {
    if (!isMoveInteraction) return false;
    if (!segment?.fen) return false;
    if (isLockedByWrongAttempt) return false;
    if (pendingConfirm) return false;

    const fromSq = String(sourceSquare || '').toLowerCase();
    const toSq = String(targetSquare || '').toLowerCase();
    if (!fromSq || !toSq || fromSq === toSq) return false;

    setSelectionSquareStyles({});
    setSelectedMoveSource('');
    setMoveFeedback(null);

    if (hasExpectedMove) {
      const expectedFrom = String(expectedMove?.from || '').toLowerCase();
      const expectedTo = String(expectedMove?.to || '').toLowerCase();
      if (fromSq === expectedFrom && toSq === expectedTo) {
        setHasAttemptedMove(true);
        setPuzzleTries((prev) => prev + 1);
        if (interaction?.confirmCapture) {
          const attempted = moveInPlacement(boardPosition, fromSq, toSq, expectedMove?.promotion);
          setPositionOverride(attempted);
          setPendingConfirm({ from: fromSq, to: toSq, text: confirmText });
          return true;
        }
        let resolvedPlacement = moveInPlacement(boardPosition, fromSq, toSq, expectedMove?.promotion);
        let nextGuidedMoveIndex = guidedMoveIndex + 1;
        const movedPiece = getPieceAt(boardPosition, fromSq);
        const learnerColor = movedPiece
          ? (movedPiece === movedPiece.toUpperCase() ? 'w' : 'b')
          : null;

        // Some lesson lines are authored as full continuations (both sides).
        // Auto-play opponent replies so the learner only needs to play their own moves.
        while (
          learnerColor &&
          nextGuidedMoveIndex < guidedMoves.length
        ) {
          const autoMove = guidedMoves[nextGuidedMoveIndex];
          if (!autoMove?.from || !autoMove?.to) break;
          const autoPiece = getPieceAt(resolvedPlacement, autoMove.from);
          if (!autoPiece) break;
          const autoColor = autoPiece === autoPiece.toUpperCase() ? 'w' : 'b';
          if (autoColor === learnerColor) break;
          resolvedPlacement = moveInPlacement(
            resolvedPlacement,
            autoMove.from,
            autoMove.to,
            autoMove.promotion
          );
          nextGuidedMoveIndex += 1;
        }

        const isSequenceDone = nextGuidedMoveIndex >= guidedMoves.length;
        setGuidedMoveIndex(nextGuidedMoveIndex);
        applyCorrectGuidedMove({
          sourceSquare: fromSq,
          targetSquare: toSq,
          promotion: expectedMove?.promotion,
          sequenceDone: isSequenceDone,
          resultPlacement: resolvedPlacement
        });
        if (!isSequenceDone) {
          const nextExpected = guidedMoves[nextGuidedMoveIndex];
          if (nextExpected?.from && nextExpected?.to && (!isPuzzleFlow || solutionVisible)) {
            setBoardArrows([[nextExpected.from, nextExpected.to]]);
            setArrowColor('rgb(37,99,235)');
            setSelectionSquareStyles({
              [nextExpected.from]: {
                boxShadow: 'inset 0 0 0 3px rgba(37,99,235,0.9)',
                background: 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.24) 0%, rgba(59,130,246,0.06) 60%, rgba(59,130,246,0) 76%)'
              },
              [nextExpected.to]: {
                boxShadow: 'inset 0 0 0 3px rgba(16,185,129,0.9)',
                background: 'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0.12) 48%, rgba(16,185,129,0) 76%)'
              }
            });
          }
        }
        return true;
      }
    }

    const legalTargets = getPseudoLegalMovesForSquare(currentFenForMoveRules, fromSq, {
      respectTurn: false
    });
    const isLegalMove = legalTargets.includes(toSq);

    if (!isLegalMove) {
      const msg =
        language === 'ro'
          ? 'Mutare ilegală pentru această piesă. Mai încearcă o dată!'
          : 'Illegal move for this piece. Give it another try!';
      setMoveFeedback({ status: 'wrong', message: msg });
      playErrorSound();
      return false;
    }

    setHasAttemptedMove(true);
    if (hasExpectedMove) {
      setPuzzleTries((prev) => prev + 1);
    }

    if (!hasExpectedMove) {
      const moved = moveInPlacement(boardPosition, fromSq, toSq);
      setPositionOverride(moved);
      const msg =
        language === 'ro'
          ? 'Mutare legală. Continuă explorarea sau mergi la pasul următor.'
          : 'Legal move. Continue exploring or move to the next step.';
      setMoveFeedback({ status: 'correct', message: msg });
      playMoveSound();
      return true;
    }

    const isExpected =
      String(expectedMove.from || '').toLowerCase() === fromSq &&
      String(expectedMove.to || '').toLowerCase() === toSq;

    if (!isExpected) {
      const attempted = moveInPlacement(boardPosition, fromSq, toSq);
      setPositionOverride(attempted);
      const nextWrongStreak = isPuzzleFlow ? puzzleWrongStreak + 1 : puzzleWrongStreak;
      const msg =
        language === 'ro'
          ? 'Interesant, dar cred că există o variantă și mai bună. Mai încearcă o dată!'
          : 'Interesting, but I think there’s a better move. Give it another go!';
      setMoveFeedback({ status: 'wrong', message: msg });
      setOverlayForExpected('wrong');
      playErrorSound();
      const nudge =
        language === 'ro'
          ? 'Interesant, dar cred că există o variantă și mai bună. Mai încearcă o dată!'
          : 'Interesting, but I think there’s a better move. Give it another go!';
      speakFeedback(nudge);
      if (isPuzzleFlow) {
        setPuzzleWrongStreak(nextWrongStreak);
        if (nextWrongStreak >= 3 && !puzzleOutcomeSent) {
          setPuzzleOutcomeSent(true);
          const baseFailMsg =
            language === 'ro'
              ? 'Ai avut 3 încercări greșite consecutive.'
              : 'You had 3 consecutive wrong attempts.';
          setMoveFeedback({ status: 'wrong', message: baseFailMsg });
          void awardPuzzleNow({
            result: 'fail3',
            playedMoveUci: '',
            tries: Math.max(3, Number(puzzleTries || 0)),
            usedSolution: false
          }).then((award) => {
            const ratingDelta = Number(award?.ratingDelta || 0);
            const pointsDelta = Number(award?.pointsDelta || 0);
            const pretty = (n) => (n > 0 ? `+${n}` : `${n}`);
            const fullFailMsg =
              language === 'ro'
                ? `Ai avut 3 încercări greșite consecutive. Penalizare: Rating ${pretty(ratingDelta)} și IQ Points ${pretty(pointsDelta)}.`
                : `You had 3 consecutive wrong attempts. Penalty: Rating ${pretty(ratingDelta)} and IQ Points ${pretty(pointsDelta)}.`;
            setMoveFeedback({ status: 'wrong', message: fullFailMsg });
          });
          return false;
        }
        window.setTimeout(() => setPositionOverride(null), 140);
        return false;
      }
      return true;
    }

    if (interaction?.confirmCapture) {
      const attempted = moveInPlacement(boardPosition, fromSq, toSq, expectedMove?.promotion);
      setPositionOverride(attempted);
      setPendingConfirm({ from: fromSq, to: toSq, text: confirmText });
      return true;
    }

    applyCorrectGuidedMove({ sourceSquare: fromSq, targetSquare: toSq, sequenceDone: true });
    return true;
  };

  const resetDragState = useCallback(() => {
    dragSourceRef.current = '';
    dragStateRef.current = { source: '', target: '', dropHandled: false };
  }, []);

  const handlePieceDragBegin = (piece, sourceSquare) => {
    if (!isGuidedMoveStep) return;
    if (isLockedByWrongAttempt || pendingConfirm) return;

    const sq = String(sourceSquare || '').trim().toLowerCase();
    if (!sq) return;

    dragSourceRef.current = sq;
    dragStateRef.current = { source: sq, target: '', dropHandled: false };
    // Keep drag begin side-effect free; updating board styles here can interrupt
    // drop handling in some browsers (notably Safari).
  };

  const prepareGuidedMoveSource = (square) => {
    const sq = String(square || '').trim().toLowerCase();
    if (!sq) return false;

    // For puzzle flow, keep hints hidden until user explicitly reveals solution.
    if (isPuzzleFlow && !solutionVisible) {
      setBoardArrows([]);
      setArrowColor('rgb(255,170,0)');
      setSquareStyles({});
    } else {
      setBoardArrows(baseStepAnnotations.arrows || []);
      setArrowColor(baseStepAnnotations.arrowColor || 'rgb(255,170,0)');
      setSquareStyles(baseStepAnnotations.squareStyles || {});
    }

    const piece = getPieceAt(currentFenForMoveRules, sq);
    if (!piece) {
      setSelectionSquareStyles({});
      setSelectedMoveSource('');
      return false;
    }

    const legalTargets = getPseudoLegalMovesForSquare(currentFenForMoveRules, sq, {
      // Lesson steps can include custom teaching FENs; keep this permissive.
      respectTurn: false
    });

    const nextStyles = {
      [sq]: {
        boxShadow: 'inset 0 0 0 3px rgba(37,99,235,0.9)',
        background:
          'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.28) 0%, rgba(59,130,246,0.08) 62%, rgba(59,130,246,0) 72%)'
      }
    };

    for (let i = 0; i < legalTargets.length; i += 1) {
      const target = legalTargets[i];
      nextStyles[target] = {
        boxShadow: 'inset 0 0 0 2px rgba(16,185,129,0.9)',
        background:
          'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.45) 0%, rgba(16,185,129,0.2) 28%, rgba(16,185,129,0.05) 62%, rgba(16,185,129,0) 76%)'
      };
    }

    setSelectionSquareStyles(nextStyles);
    setSelectedMoveSource(sq);
    return true;
  };

  const inferDropSourceFromTarget = useCallback((targetSquare, draggedPiece) => {
    const target = String(targetSquare || '').trim().toLowerCase();
    if (!target) return '';

    const parsed = parseFen(currentFenForMoveRules);
    if (!parsed?.board) return '';

    const candidates = [];
    const normalizedDraggedPiece = String(draggedPiece || '').trim();
    for (let y = 0; y < 8; y += 1) {
      for (let x = 0; x < 8; x += 1) {
        const piece = parsed.board[y]?.[x];
        if (!piece) continue;
        const sq = coordsToSquare(x, y);
        if (!sq) continue;
        const legalTargets = getPseudoLegalMovesForSquare(currentFenForMoveRules, sq, {
          respectTurn: false
        });
        if (legalTargets.includes(target)) {
          const isWhite = piece === piece.toUpperCase();
          const pieceType = String(piece).toUpperCase();
          const boardCode = `${isWhite ? 'w' : 'b'}${pieceType}`;
          candidates.push({ sq, boardCode });
        }
      }
    }

    if (!candidates.length) return '';

    if (normalizedDraggedPiece) {
      const pieceMatched = candidates.filter((entry) => entry.boardCode === normalizedDraggedPiece);
      if (pieceMatched.length === 1) return pieceMatched[0].sq;
      if (pieceMatched.length > 1) {
        const selectedFromMatched = String(selectedMoveSource || '').trim().toLowerCase();
        if (selectedFromMatched && pieceMatched.some((entry) => entry.sq === selectedFromMatched)) {
          return selectedFromMatched;
        }
      }
    }

    const selected = String(selectedMoveSource || '').trim().toLowerCase();
    if (selected && candidates.some((entry) => entry.sq === selected)) return selected;

    if (hasExpectedMove) {
      const expectedFrom = String(expectedMove?.from || '').trim().toLowerCase();
      if (expectedFrom && candidates.some((entry) => entry.sq === expectedFrom)) return expectedFrom;
    }

    if (candidates.length === 1) return candidates[0].sq;
    return '';
  }, [
    currentFenForMoveRules,
    selectedMoveSource,
    hasExpectedMove,
    expectedMove
  ]);

  const resolveAndApplyDraggedMove = (sourceSquare, targetSquare, piece) => {
    let resolvedTarget = String(targetSquare || '').trim().toLowerCase();
    let resolvedSource = String(sourceSquare || '').trim().toLowerCase()
      || dragSourceRef.current
      || String(dragStateRef.current?.source || '').trim().toLowerCase()
      || String(selectedMoveSource || '').trim().toLowerCase();

    if (isGuidedMoveStep && hasExpectedMove) {
      const expectedFrom = String(expectedMove?.from || '').trim().toLowerCase();
      const expectedTo = String(expectedMove?.to || '').trim().toLowerCase();

      // react-chessboard can miss sourceSquare on first drag in some browsers.
      // If target matches expected move target, force expected source.
      if (resolvedTarget && expectedTo === resolvedTarget && expectedFrom) {
        resolvedSource = expectedFrom;
      }

      // Defensive: some integrations can report source/target swapped.
      if (
        resolvedSource &&
        resolvedTarget &&
        resolvedSource === expectedTo &&
        resolvedTarget === expectedFrom
      ) {
        const tmp = resolvedSource;
        resolvedSource = resolvedTarget;
        resolvedTarget = tmp;
      }
    }

    // Defensive fallback for browsers/devices where onPieceDrop intermittently
    // misses sourceSquare on first drag.
    if (!resolvedSource && resolvedTarget) {
      resolvedSource = inferDropSourceFromTarget(resolvedTarget, piece);
    }

    if (!resolvedSource || !resolvedTarget) return false;
    if (resolvedSource === resolvedTarget) return false;
    prepareGuidedMoveSource(resolvedSource);
    return handleGuidedMoveAttempt(resolvedSource, resolvedTarget);
  };

  const handlePieceDrop = (sourceSquare, targetSquare, piece) => {
    dragStateRef.current = {
      source: String(sourceSquare || dragStateRef.current?.source || '').trim().toLowerCase(),
      target: String(targetSquare || dragStateRef.current?.target || '').trim().toLowerCase(),
      dropHandled: true
    };
    const ok = resolveAndApplyDraggedMove(sourceSquare, targetSquare, piece);
    resetDragState();
    return ok;
  };

  const handleDragOverSquare = (square) => {
    if (!isGuidedMoveStep) return;
    const sq = String(square || '').trim().toLowerCase();
    if (!sq) return;
    dragStateRef.current = {
      ...dragStateRef.current,
      target: sq
    };
  };

  const handlePieceDragEnd = (piece, sourceSquare) => {
    if (!isGuidedMoveStep || isLockedByWrongAttempt || pendingConfirm) {
      resetDragState();
      return;
    }

    if (dragStateRef.current?.dropHandled) {
      resetDragState();
      return;
    }

    const fallbackSource = String(sourceSquare || dragStateRef.current?.source || '').trim().toLowerCase();
    const fallbackTarget = String(dragStateRef.current?.target || '').trim().toLowerCase();
    if (!fallbackSource || !fallbackTarget || fallbackSource === fallbackTarget) {
      resetDragState();
      return;
    }

    resolveAndApplyDraggedMove(fallbackSource, fallbackTarget, piece);
    resetDragState();
  };

  const actionTargetSquare =
    interaction?.type === 'action' ? String(interaction?.targetSquare || '').trim().toLowerCase() : '';
  const actionKind =
    interaction?.type === 'action' ? String(interaction?.action || '').trim().toLowerCase() : '';

  const actionSquareStyles = useMemo(() => {
    if (interaction?.type !== 'action') return {};
    const styles = {};
    if (actionTargetSquare) {
      styles[actionTargetSquare] = {
        boxShadow: actionCompleted
          ? 'inset 0 0 0 3px rgba(34,197,94,0.85)'
          : 'inset 0 0 0 3px rgba(245,158,11,0.92)',
        background: actionCompleted ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.14)'
      };
    }
    return styles;
  }, [interaction, actionTargetSquare, actionCompleted]);

  const handleMoveSquareSelect = (square) => {
    if (!isGuidedMoveStep) return;
    if (isLockedByWrongAttempt || pendingConfirm) return;
    const sq = String(square || '').trim().toLowerCase();
    if (!sq) return;

    // For puzzle flow, keep hints hidden until user explicitly reveals solution.
    if (isPuzzleFlow && !solutionVisible) {
      setBoardArrows([]);
      setArrowColor('rgb(255,170,0)');
      setSquareStyles({});
    } else {
      setBoardArrows(baseStepAnnotations.arrows || []);
      setArrowColor(baseStepAnnotations.arrowColor || 'rgb(255,170,0)');
      setSquareStyles(baseStepAnnotations.squareStyles || {});
    }

    if (selectedMoveSource && sq !== selectedMoveSource) {
      handleGuidedMoveAttempt(selectedMoveSource, sq);
      return;
    }

    prepareGuidedMoveSource(sq);
  };

  const effectiveSquareStyles = useMemo(() => {
    return {
      ...(squareStyles || {}),
      ...(actionSquareStyles || {}),
      ...(selectionSquareStyles || {}),
      ...(transientSquareStyles || {})
    };
  }, [squareStyles, actionSquareStyles, selectionSquareStyles, transientSquareStyles]);

  const boardDimmed = interaction?.type === 'action' && actionKind === 'handshake' && !actionCompleted;
  const showFocusNoise = interaction?.type === 'action' && actionKind === 'focus' && !focusModeOn;

  const handleActionSquareClick = (sq) => {
    if (interaction?.type !== 'action') return;
    if (actionCompleted) return;
    if (!sq) return;

    const square = String(sq).trim().toLowerCase();

    // For puzzle flow, keep hints hidden until user explicitly reveals solution.
    if (isPuzzleFlow && !solutionVisible) {
      setBoardArrows([]);
      setArrowColor('rgb(255,170,0)');
      setSquareStyles({});
    } else {
      setBoardArrows(baseStepAnnotations.arrows || []);
      setArrowColor(baseStepAnnotations.arrowColor || 'rgb(255,170,0)');
      setSquareStyles(baseStepAnnotations.squareStyles || {});
    }

    if (actionKind === 'tap_square' || actionKind === 'tap' || actionKind === 'square') {
      if (square !== actionTargetSquare) {
        const msg =
          language === 'ro'
            ? 'Nu acesta este pătratul periculos. Mai încearcă!'
            : 'That is not the dangerous square. Try again!';
        setActionFeedback({ status: 'warn', message: msg });
        playErrorSound();
        speakFeedback(msg);
        return;
      }

      const okMsg =
        language === 'ro'
          ? 'Exact! Ai identificat corect pătratul periculos.'
          : 'Exactly! You identified the dangerous square correctly.';
      setActionFeedback({ status: 'ok', message: okMsg });
      setActionCompleted(true);
      setBurstTrigger((x) => x + 1);
      playSuccessSound();
      speakFeedback(okMsg);
      return;
    }

    if (actionKind === 'touch_move') {
      if (square !== actionTargetSquare) {
        const msg =
          language === 'ro'
            ? 'Ai atins altă piesă/pătrat. Gândește cu mintea, apoi atinge piesa cerută.'
            : 'You touched a different piece/square. Think first, then touch the requested piece.';
        setActionFeedback({ status: 'warn', message: msg });
        playErrorSound();
        speakFeedback(msg);
        return;
      }

      const okMsg =
        language === 'ro'
          ? 'Perfect. Ai atins piesa corectă. Acum ești obligat să o muți.'
          : 'Perfect. You touched the correct piece. Now you must move it.';
      setActionFeedback({ status: 'ok', message: okMsg });
      setActionCompleted(true);
      setBurstTrigger((x) => x + 1);
      playSuccessSound();
      speakFeedback(okMsg);
      return;
    }

    if (actionKind === 'adjust') {
      if (!adjustArmed) {
        const msg =
          language === 'ro'
            ? 'Mai întâi apasă „Aranjez”, apoi atinge piesa.'
            : 'First press “Adjust”, then touch the piece.';
        setActionFeedback({ status: 'warn', message: msg });
        playErrorSound();
        speakFeedback(msg);
        return;
      }
      if (square !== actionTargetSquare) {
        const msg =
          language === 'ro'
            ? 'Atinge piesa evidențiată ca să o aranjezi.'
            : 'Touch the highlighted piece to adjust it.';
        setActionFeedback({ status: 'warn', message: msg });
        playErrorSound();
        speakFeedback(msg);
        return;
      }

      const okMsg =
        language === 'ro'
          ? 'Super. Ai folosit „Aranjez” corect.'
          : 'Great. You used “Adjust” correctly.';
      setActionFeedback({ status: 'ok', message: okMsg });
      setActionCompleted(true);
      setAdjustArmed(false);
      setBurstTrigger((x) => x + 1);
      playSuccessSound();
      speakFeedback(okMsg);
    }
  };

  const handleCloseClick = () => {
    if (
      isPuzzleFlow &&
      !puzzleOutcomeSent &&
      !isMoveCorrect &&
      typeof onComplete === 'function'
    ) {
      setPuzzleOutcomeSent(true);
      onComplete({
        lessonSlug: lessonData?.id || null,
        lessonDbId: lessonData?.lessonDbId || null,
        lessonTitle: lessonData?.title || null,
        puzzleCatalogId: lessonData?.puzzleCatalogId || null,
        puzzleResult: 'abandon',
        playedMoveUci: '',
        tries: Math.max(1, Number(puzzleTries || 1)),
        usedSolution: Boolean(solutionVisible),
        solveMs: Math.max(0, Date.now() - Number(segmentStartedAt || Date.now())),
        continueTraining: false
      });
      return;
    }
    onClose();
  };
  const handleToggleCctOption = (id) => {
    if (!isCctInteraction || actionCompleted) return;
    const optionId = String(id || '').trim();
    if (!optionId) return;
    setCctSubmitted(false);
    setActionFeedback(null);
    setCctSelectedIds((prev) => (
      prev.includes(optionId)
        ? prev.filter((x) => x !== optionId)
        : [...prev, optionId]
    ));
  };

  const handleValidateCct = () => {
    if (!isCctInteraction) return;
    const selected = [...cctSelectedIds].sort();
    const correct = [...cctCorrectIds].sort();
    const isMatch = selected.length === correct.length && selected.every((id, i) => id === correct[i]);
    setCctSubmitted(true);
    if (isMatch) {
      const okMsg =
        language === 'ro'
          ? 'Perfect. Ai trecut corect prin filtrul SCA: verifici șahuri, capturi și amenințări înainte de mutare.'
          : 'Perfect. You passed the CCT filter correctly: checks, captures, and threats before moving.';
      setActionCompleted(true);
      setActionFeedback({ status: 'ok', message: okMsg });
      setBurstTrigger((x) => x + 1);
      playSuccessSound();
      return;
    }
    const warnMsg =
      language === 'ro'
        ? 'Încă nu e corect. Reia SCA în ordine: șahuri candidate, capturi candidate, amenințări candidate.'
        : 'Not correct yet. Re-run CCT in order: candidate checks, candidate captures, candidate threats.';
    setActionCompleted(false);
    setActionFeedback({ status: 'warn', message: warnMsg });
    playErrorSound();
  };

  const renderPuzzleDeltaChips = (ratingDeltaRaw, pointsDeltaRaw) => {
    const metricItems = [
      {
        key: 'rating',
        value: Number(ratingDeltaRaw || 0),
        labelRo: 'Rating',
        labelEn: 'Rating'
      },
      {
        key: 'points',
        value: Number(pointsDeltaRaw || 0),
        labelRo: 'IQ Points',
        labelEn: 'IQ Points'
      }
    ];

    return (
      <div className="puzzle-outcome-deltas">
        {metricItems.map((metric) => {
          const direction = metric.value > 0 ? 'up' : metric.value < 0 ? 'down' : 'flat';
          const signedValue = metric.value > 0 ? `+${metric.value}` : `${metric.value}`;
          const directionText = direction === 'up'
            ? (language === 'ro' ? 'câștigate' : 'gained')
            : direction === 'down'
              ? (language === 'ro' ? 'pierdute' : 'lost')
              : (language === 'ro' ? 'fără schimbare' : 'no change');

          return (
            <span
              key={metric.key}
              className={`puzzle-outcome-delta-chip puzzle-outcome-delta-${direction}`}
            >
              <span className="puzzle-outcome-delta-arrow" aria-hidden="true">
                {direction === 'up' ? '▲' : direction === 'down' ? '▼' : '•'}
              </span>
              <span>
                {(language === 'ro' ? metric.labelRo : metric.labelEn)} {directionText}: {signedValue}
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="lesson-viewer">
      <div className="lesson-header">
        <button className="btn-close" onClick={handleCloseClick}>
          ← {language === 'ro' ? 'Înapoi' : 'Back'}
        </button>
        <h2 className="lesson-title">{lessonData?.title || (language === 'ro' ? 'Lecție' : 'Lesson')}</h2>
        {isPuzzleFlow ? (
          <div className="lesson-puzzle-stats-header">
            <span className="puzzle-meta-chip">
              <span className="puzzle-meta-icon" aria-hidden="true">📈</span>
              <span>{language === 'ro' ? 'Puzzle Rating:' : 'Puzzle Rating:'} {puzzleHeaderRating}</span>
            </span>
            <span className="puzzle-meta-chip">
              <span className="puzzle-meta-icon" aria-hidden="true">🏆</span>
              <span>{language === 'ro' ? 'Puzzle Points:' : 'Puzzle Points:'} {puzzleHeaderPoints}</span>
            </span>
            <span className="puzzle-meta-chip">
              <span className="puzzle-meta-icon" aria-hidden="true">⚡</span>
              <span>{language === 'ro' ? 'IQ Points:' : 'IQ Points:'} {puzzleHeaderIq}</span>
            </span>
            <span className="puzzle-meta-chip">
              <span className="puzzle-meta-icon" aria-hidden="true">♟</span>
              <span>{puzzleHeaderProfile}</span>
            </span>
            <span className="puzzle-meta-chip">
              <span className="puzzle-meta-icon" aria-hidden="true">#</span>
              <span>
                {language === 'ro' ? 'Rank general:' : 'Global rank:'}{' '}
                {puzzleHeaderRank ? `#${puzzleHeaderRank}` : (language === 'ro' ? 'în calcul' : 'calculating')}
              </span>
            </span>
            {puzzlePrimaryTheme && (
              <span className="puzzle-meta-chip">
                <span className="puzzle-meta-icon" aria-hidden="true">🏷️</span>
                <span>{language === 'ro' ? 'Categorie:' : 'Category:'} {puzzlePrimaryTheme}</span>
              </span>
            )}
          </div>
        ) : (
          <div className="lesson-progress">
            <div className="lesson-tts">
              <button
                className={`lesson-tts-btn ${ttsEnabled ? 'on' : 'off'}`}
                onClick={() => {
                  const next = !ttsEnabled;
                  setTtsEnabled(next);
                  setTTSEnabled(next);
                  if (!next) stopTTS();
                }}
                title={language === 'ro' ? 'Audio (text-to-speech)' : 'Audio (text-to-speech)'}
              >
                {language === 'ro' ? (ttsEnabled ? 'Voce: ON' : 'Voce: OFF') : (ttsEnabled ? 'Voice: ON' : 'Voice: OFF')}
              </button>

              <button
                className="lesson-tts-btn ghost"
                onClick={() => segment?.text && playNarration({ text: cleanLessonText(segment.text), audioUrl: segment.audioUrl })}
                disabled={!segment?.text}
                title={language === 'ro' ? 'Repetă' : 'Repeat'}
              >
                {language === 'ro' ? 'Repetă' : 'Repeat'}
              </button>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="progress-text">
              {segments.length ? `${currentSegment + 1} / ${segments.length}` : '—'}
            </span>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="lesson-text-container" style={{ marginBottom: '1rem' }}>
          {language === 'ro' ? 'Se încarcă lecția...' : 'Loading lesson...'}
        </div>
      )}

      {error && (
        <div className="lesson-text-container" style={{ marginBottom: '1rem' }}>
          <strong>{language === 'ro' ? 'Eroare:' : 'Error:'}</strong> {error}
        </div>
      )}

      {!user?.id && (
        <div className="lesson-text-container" style={{ marginBottom: '1rem' }}>
          <strong>{language === 'ro' ? 'Progres:' : 'Progress:'}</strong>{' '}
          {language === 'ro'
            ? 'Autentifică-te ca să salvăm progresul.'
            : 'Sign in to save progress.'}{' '}
          <button className="btn-card" onClick={onRequestLogin}>
            {language === 'ro' ? 'Autentificare' : 'Sign in'}
          </button>
        </div>
      )}

      <div className="lesson-content">
        <div className="board-container">
          <div className="board-overlay">
            <SuccessBurst trigger={burstTrigger} />
            {showFocusNoise && (
              <div className="cm-focus-noise" aria-hidden="true">
                <div className="cm-focus-bubble b1">{language === 'ro' ? 'Notificare' : 'Notification'}</div>
                <div className="cm-focus-bubble b2">{language === 'ro' ? 'Mesaj nou' : 'New message'}</div>
                <div className="cm-focus-bubble b3">{language === 'ro' ? 'Ping' : 'Ping'}</div>
              </div>
            )}
            {sparkRainOn && (
              <div className="cm-spark-rain" key={sparkRainNonce} aria-hidden="true">
                {Array.from({ length: 18 }).map((_, i) => (
                  <span key={i} className={`cm-spark s${i % 6}`} style={{ '--i': i }} />
                ))}
              </div>
            )}
          </div>
          <div className="board-wrap" ref={boardWrapRef}>
            {canShowBoard ? (
              <Chessboard
                id={`lesson-board-${lessonSlug || 'unknown'}-${currentSegment}`}
                position={boardPosition}
                boardOrientation={boardOrientation}
                boardWidth={boardWidth}
                arePiecesDraggable={isGuidedMoveStep && !isLockedByWrongAttempt && !pendingConfirm}
                arePremovesAllowed={false}
                areArrowsAllowed={false}
                customArrows={boardArrows}
                customArrowColor={arrowColor}
                customSquareStyles={effectiveSquareStyles}
                onSquareClick={(sq) => {
                  if (interaction?.type === 'action') {
                    handleActionSquareClick(sq);
                    return;
                  }
                  if (isMoveInteraction) {
                    handleMoveSquareSelect(sq);
                    return;
                  }
                  // Keep puzzle hints hidden unless solution is explicitly revealed.
                  if (isPuzzleFlow && !solutionVisible) {
                    setBoardArrows([]);
                    setArrowColor('rgb(255,170,0)');
                    setSquareStyles({});
                  } else {
                    setBoardArrows(baseStepAnnotations.arrows || []);
                    setArrowColor(baseStepAnnotations.arrowColor || 'rgb(255,170,0)');
                    setSquareStyles(baseStepAnnotations.squareStyles || {});
                  }
                }}
                isDraggablePiece={({ sourceSquare }) => {
                  if (!isGuidedMoveStep) return false;
                  if (isLockedByWrongAttempt) return false;
                  if (pendingConfirm) return false;
                  // Keep drag permissive; legality is validated on drop.
                  // This avoids touch/drag edge cases where sourceSquare is not provided consistently.
                  return true;
                }}
                onPieceDragBegin={handlePieceDragBegin}
                onDragOverSquare={handleDragOverSquare}
                onPieceDragEnd={handlePieceDragEnd}
                onPieceDrop={handlePieceDrop}
                customBoardStyle={{
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  filter: boardDimmed ? 'grayscale(1) saturate(0.85)' : 'none',
                  transition: 'filter 220ms ease'
                }}
              />
            ) : (
              <div
                style={{
                  width: boardWidth,
                  height: boardWidth,
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0.25))',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}
              />
            )}
          </div>

          {debugEnabled && (
            <div
              style={{
                marginTop: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 12,
                color: '#334155',
                opacity: 0.95
              }}
            >
              <div>fen: {String(segment?.fen || '—')}</div>
              <div>interaction: {interaction ? JSON.stringify(interaction) : '—'}</div>
              <div>expected: {expectedMove ? JSON.stringify(expectedMove) : '—'}</div>
              <div>guided: {String(isGuidedMoveStep)}</div>
            </div>
          )}
        </div>

        <div className="lesson-text-container">
          <div className="segment-number">
            {stepNumberLabel}
            {shouldAppendStepTitle ? <span className="segment-title"> · {stepTitleText}</span> : null}
          </div>
          <div className="segment-text">
            {cleanLessonText(segment?.text) || (isLoading ? '' : '—')}
          </div>
          {isPuzzleFlow && puzzleAwardResult && (
            <div className={`puzzle-outcome-badge ${
              String(puzzleAwardResult.result || '').toLowerCase() === 'success_hint'
                ? 'puzzle-outcome-hint'
                : String(puzzleAwardResult.result || '').toLowerCase() === 'success'
                  ? 'puzzle-outcome-success'
                  : String(puzzleAwardResult.result || '').toLowerCase() === 'fail3'
                    ? 'puzzle-outcome-fail'
                    : String(puzzleAwardResult.result || '').toLowerCase() === 'abandon'
                      ? 'puzzle-outcome-abandon'
                      : 'puzzle-outcome-neutral'
            }`}>
              <span className="puzzle-outcome-title">
                {String(puzzleAwardResult.result || '').toLowerCase() === 'success'
                  ? (language === 'ro' ? 'Ai rezolvat cu succes' : 'Solved successfully')
                  : String(puzzleAwardResult.result || '').toLowerCase() === 'success_hint'
                    ? (language === 'ro' ? 'Rezolvat cu ajutor' : 'Solved with hint')
                    : String(puzzleAwardResult.result || '').toLowerCase() === 'already_solved'
                      ? (language === 'ro' ? 'Deja rezolvat (fără puncte bonus)' : 'Already solved (no bonus points)')
                    : String(puzzleAwardResult.result || '').toLowerCase() === 'fail3'
                      ? (language === 'ro' ? 'Ai ratat după 3 încercări' : 'Failed after 3 tries')
                      : String(puzzleAwardResult.result || '').toLowerCase() === 'abandon'
                        ? (language === 'ro' ? 'Puzzle abandonat' : 'Puzzle abandoned')
                      : (language === 'ro' ? 'Rezultat actualizat' : 'Result updated')}
              </span>
              {renderPuzzleDeltaChips(
                Number(puzzleAwardResult.ratingDelta || 0),
                Number(puzzleAwardResult.pointsDelta || 0)
              )}
            </div>
          )}
          {puzzleOutcomeUi && !puzzleAwardResult && (
            <div className={`puzzle-outcome-badge ${puzzleOutcomeUi.cls}`}>
              <span className="puzzle-outcome-title">{puzzleOutcomeUi.title}</span>
              <span>{puzzleOutcomeUi.detailsText}</span>
              {renderPuzzleDeltaChips(
                Number(puzzleLastOutcome?.ratingDelta || 0),
                Number(puzzleLastOutcome?.pointsDelta || 0)
              )}
            </div>
          )}

          {isPuzzleFlow && puzzleMeta && (
            <div className="lesson-preview-meta puzzle-meta-grid" style={{ marginBottom: '1rem' }}>
              <span className="puzzle-meta-chip">
                <span className="puzzle-meta-icon" aria-hidden="true">⏱</span>
                <span>{language === 'ro' ? '~2 min' : '~2 min'}</span>
              </span>
              <span className={`puzzle-meta-chip ${puzzleDifficultyClass}`}>
                <span className="puzzle-meta-icon" aria-hidden="true">{puzzleDifficultyIcon}</span>
                <span>{language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {puzzleDifficulty}</span>
              </span>
              <span className={`puzzle-meta-chip ${puzzleRatingClass}`}>
                <span className="puzzle-meta-icon" aria-hidden="true">🎯</span>
                <span>{language === 'ro' ? 'Rating:' : 'Rating:'} {puzzleRating}</span>
              </span>
              {Number.isFinite(Number(puzzleMeta?.nbPlays)) && (
                <span className="puzzle-meta-chip">
                  <span className="puzzle-meta-icon" aria-hidden="true">👥</span>
                  <span>{language === 'ro' ? 'Jucat de:' : 'Played by:'} {Number(puzzleMeta.nbPlays)}</span>
                </span>
              )}
              {Number.isFinite(Number(puzzleMeta?.popularity)) && (
                <span className="puzzle-meta-chip">
                  <span className="puzzle-meta-icon" aria-hidden="true">🔥</span>
                  <span>{language === 'ro' ? 'Popularitate:' : 'Popularity:'} {Number(puzzleMeta.popularity)}</span>
                </span>
              )}
            </div>
          )}

          {(interaction?.type === 'move' || interaction?.type === 'action' || interaction?.type === 'cct_check') && (
            <div className="lesson-task">
              <div className="lesson-task-title">
                {language === 'ro' ? 'Misiune' : 'Mission'}
              </div>
              <div className="lesson-task-text">
                {missionText ||
                  (interaction?.type === 'move'
                    ? language === 'ro'
                      ? 'Găsește mutarea câștigătoare și pune-o în practică pe tablă!'
                      : 'Find the winning move and play it on the board!'
                    : language === 'ro'
                      ? interaction?.type === 'cct_check'
                        ? 'Aplică filtrul SCA înainte de mutare: șahuri, capturi, amenințări.'
                        : 'Fă acțiunea cerută și apoi mergem mai departe.'
                      : interaction?.type === 'cct_check'
                        ? 'Apply the CCT filter before moving: checks, captures, threats.'
                        : 'Complete the requested action, then continue.')}
              </div>

              {interaction?.type === 'action' && (
                <div className="lesson-action-ui">
                  {actionKind === 'handshake' && (
                    <button
                      className="btn-card lesson-action-primary"
                      onClick={() => {
                        const okMsg =
                          language === 'ro'
                            ? 'Perfect. Ai salutat ca un campion.'
                            : 'Perfect. You greeted like a champion.';
                        setActionCompleted(true);
                        setActionFeedback({ status: 'ok', message: okMsg });
                        setBurstTrigger((x) => x + 1);
                        playSuccessSound();
                        speakFeedback(okMsg);
                      }}
                      disabled={actionCompleted}
                    >
                      {language === 'ro' ? 'Dau mâna' : 'Shake hands'}
                    </button>
                  )}

                  {actionKind === 'focus' && (
                    <button
                      className={`btn-card lesson-action-primary ${focusModeOn ? 'active' : ''}`}
                      onClick={() => {
                        const next = !focusModeOn;
                        setFocusModeOn(next);
                        if (next) {
                          const okMsg =
                            language === 'ro'
                              ? 'Mod de Concentrare activat. Acum vedem doar tabla.'
                              : 'Focus Mode enabled. Now we see only the board.';
                          setActionCompleted(true);
                          setActionFeedback({ status: 'ok', message: okMsg });
                          setBurstTrigger((x) => x + 1);
                          playSuccessSound();
                          speakFeedback(okMsg);
                        }
                      }}
                      disabled={actionCompleted}
                    >
                      {language === 'ro'
                        ? focusModeOn
                          ? 'Mod de Concentrare: ON'
                          : 'Activează Modul de Concentrare'
                        : focusModeOn
                          ? 'Focus Mode: ON'
                          : 'Enable Focus Mode'}
                    </button>
                  )}

                  {actionKind === 'touch_move' && (
                    null
                  )}

                  {actionKind === 'adjust' && (
                    <div className="lesson-action-row">
                      <button
                        className={`btn-card lesson-action-secondary ${adjustArmed ? 'active' : ''}`}
                        onClick={() => {
                          setAdjustArmed(true);
                          const msg =
                            language === 'ro'
                              ? 'Aranjez. Acum poți atinge piesa o singură dată ca să o centrezi.'
                              : 'Adjust. Now you can touch the piece once to straighten it.';
                          setActionFeedback({ status: 'ok', message: msg });
                          speakFeedback(msg);
                        }}
                        disabled={actionCompleted}
                      >
                        {language === 'ro' ? 'Aranjez (J’adoube)' : 'Adjust (J’adoube)'}
                      </button>
                    </div>
                  )}

                  {actionKind === 'sportsmanship' && (
                    <div className="lesson-action-row">
                      <button
                        className="btn-card lesson-action-primary"
                        onClick={() => {
                          const okMsg =
                            language === 'ro'
                              ? 'Felicitări! Ai arătat respect.'
                              : 'Well done! You showed respect.';
                          setActionCompleted(true);
                          setActionFeedback({ status: 'ok', message: okMsg });
                          setBurstTrigger((x) => x + 1);
                          setSparkRainOn(true);
                          setSparkRainNonce((n) => n + 1);
                          window.setTimeout(() => setSparkRainOn(false), 1200);
                          playSuccessSound();
                          speakFeedback(okMsg);
                        }}
                        disabled={actionCompleted}
                      >
                        {language === 'ro' ? 'Felicitări!' : 'Good game!'}
                      </button>
                      <button
                        className="btn-card lesson-action-secondary"
                        onClick={() => {
                          const okMsg =
                            language === 'ro'
                              ? 'Mulțumesc pentru joc. Respect.'
                              : 'Thank you for the game. Respect.';
                          setActionCompleted(true);
                          setActionFeedback({ status: 'ok', message: okMsg });
                          setBurstTrigger((x) => x + 1);
                          setSparkRainOn(true);
                          setSparkRainNonce((n) => n + 1);
                          window.setTimeout(() => setSparkRainOn(false), 1200);
                          playSuccessSound();
                          speakFeedback(okMsg);
                        }}
                        disabled={actionCompleted}
                      >
                        {language === 'ro' ? 'Mulțumesc pentru joc!' : 'Thanks for the game!'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {interaction?.type === 'cct_check' && (
                <div className="lesson-action-ui">
                  <div className="cct-options-grid">
                    {cctOptions.map((opt) => {
                      const selected = cctSelectedIds.includes(opt.id);
                      const showCorrect = cctSubmitted && cctCorrectIds.includes(opt.id);
                      const showWrong = cctSubmitted && selected && !cctCorrectIds.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          className={`cct-option-btn${selected ? ' selected' : ''}${showCorrect ? ' correct' : ''}${showWrong ? ' wrong' : ''}`}
                          onClick={() => handleToggleCctOption(opt.id)}
                          disabled={actionCompleted}
                        >
                          <span className="cct-option-badge">{opt.id.toUpperCase()}</span>
                          <span>{opt.text}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="lesson-action-row">
                    <button
                      className="btn-card lesson-action-primary"
                      onClick={handleValidateCct}
                      disabled={actionCompleted || cctOptions.length === 0}
                    >
                      {language === 'ro' ? 'Verifică filtrul SCA' : 'Validate CCT filter'}
                    </button>
                  </div>
                </div>
              )}

              {interaction?.type === 'move' && pendingConfirm && (
                <div className="lesson-confirm">
                  <div className="lesson-confirm-text">
                    {pendingConfirm.text || (language === 'ro' ? 'Ești sigur?' : 'Are you sure?')}
                  </div>
                  <div className="lesson-confirm-actions">
                    <button
                      className="btn-card lesson-action-secondary"
                      onClick={() => resetBoardToStep()}
                    >
                      {language === 'ro' ? 'Renunț' : 'Cancel'}
                    </button>
                    <button
                      className="btn-card lesson-action-primary"
                      onClick={() => {
                        const from = pendingConfirm.from;
                        const to = pendingConfirm.to;
                        setPendingConfirm(null);
                        applyCorrectGuidedMove({ sourceSquare: from, targetSquare: to });
                      }}
                    >
                      {language === 'ro' ? 'Capturez' : 'Capture'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {actionFeedback && (
            <div
              className="lesson-feedback"
              style={{
                borderColor: actionFeedback.status === 'ok' ? '#16a34a' : '#dc2626',
                background: actionFeedback.status === 'ok' ? '#ecfdf5' : '#fef2f2',
                color: actionFeedback.status === 'ok' ? '#14532d' : '#7f1d1d'
              }}
            >
              {actionFeedback.message}
            </div>
          )}

          {moveFeedback && (
            <div
              className="lesson-feedback"
              style={{
                borderColor: feedbackColors?.border,
                background: feedbackColors?.bg,
                color: feedbackColors?.text
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                <div>{moveFeedback.message}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {moveFeedback.status === 'wrong' && (
                    <button className="btn-card" onClick={resetBoardToStep}>
                      {language === 'ro' ? 'Încearcă din nou' : 'Try again'}
                    </button>
                  )}
                  {isPuzzleFlow && moveFeedback.status === 'wrong' && !solutionVisible && solutionMoves.length > 0 && (
                    <button className="btn-card" onClick={handleRevealSolution}>
                      {language === 'ro' ? 'Afișează soluția' : 'Show solution'}
                    </button>
                  )}
                  {isPuzzleFlow && solutionVisible && canApplyCurrentSolutionMove && (
                    <button className="btn-card" onClick={handleApplySolutionMove}>
                      {hasNextSolutionMove
                        ? (language === 'ro' ? 'Mutarea următoare' : 'Next move')
                        : (language === 'ro' ? 'Aplică mutarea finală' : 'Apply final move')}
                    </button>
                  )}
                </div>
              </div>
              {moveFeedback.status === 'correct' && interaction?.explanation && (
                <div style={{ marginTop: 10, lineHeight: 1.5 }}>
                  {cleanLessonText(
                    typeof interaction.explanation === 'string'
                      ? interaction.explanation
                      : (interaction.explanation?.[language] || interaction.explanation?.ro || interaction.explanation?.en || '')
                  )}
                </div>
              )}
              {structuredMoveFeedback && (
                <div className="lesson-feedback-structured">
                  {structuredMoveFeedback.why && (
                    <div className="lesson-feedback-line">
                      <strong>{language === 'ro' ? 'De ce e bună:' : 'Why it is strong:'}</strong> {structuredMoveFeedback.why}
                    </div>
                  )}
                  {structuredMoveFeedback.threat && (
                    <div className="lesson-feedback-line">
                      <strong>{language === 'ro' ? 'Ce amenință:' : 'What it threatens:'}</strong> {structuredMoveFeedback.threat}
                    </div>
                  )}
                  {structuredMoveFeedback.block && (
                    <div className="lesson-feedback-line">
                      <strong>{language === 'ro' ? 'Ce blochează:' : 'What it restricts:'}</strong> {structuredMoveFeedback.block}
                    </div>
                  )}
                  {structuredMoveFeedback.next && (
                    <div className="lesson-feedback-line">
                      <strong>{language === 'ro' ? 'Planul următor:' : 'Next plan:'}</strong> {structuredMoveFeedback.next}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {segment?.moves && segment.moves.length > 0 && (
            <div className="moves-display">
              <strong>{language === 'ro' ? 'Mutări:' : 'Moves:'}</strong> {segment.moves.join(', ')}
            </div>
          )}

          {isLastSegment && hasLessonQuiz && (
            <div className="lesson-task" style={{ marginTop: 14 }}>
              {!quizStarted ? (
                <>
                  <div className="lesson-task-title">
                    {language === 'ro' ? 'Quiz de consolidare' : 'Knowledge Quiz'}
                  </div>
                  <div className="lesson-task-text">
                    {language === 'ro'
                      ? `La final, răspunde la ${quizQuestions.length} întrebări pentru a fixa ce ai învățat în lecție.`
                      : `At the end, answer ${quizQuestions.length} questions to reinforce what you learned in this lesson.`}
                  </div>
                  <div className="lesson-action-ui" style={{ marginTop: 12 }}>
                    <button
                      className="btn-card lesson-action-primary"
                      onClick={() => {
                        setQuizStarted(true);
                        setQuizIndex(0);
                        setQuizSelectedOptionId('');
                        setQuizChecked(false);
                      }}
                    >
                      {language === 'ro' ? 'Începe quiz-ul' : 'Start quiz'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="lesson-task-title">
                    {language === 'ro'
                      ? `Întrebarea ${Math.min(quizIndex + 1, quizQuestions.length)} / ${quizQuestions.length}`
                      : `Question ${Math.min(quizIndex + 1, quizQuestions.length)} / ${quizQuestions.length}`}
                  </div>
                  {!quizCompleted && currentQuizQuestion && (
                    <>
                      <div className="lesson-task-text" style={{ marginBottom: 10 }}>
                        {currentQuizQuestion.question}
                      </div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {(currentQuizQuestion.options || []).map((opt) => {
                          const oid = String(opt?.id || '');
                          const selected = quizSelectedOptionId === oid;
                          const isCorrect = oid === String(currentQuizQuestion.correctOptionId || '');
                          const isWrongSelected = quizChecked && selected && !isCorrect;
                          const isCorrectSelected = quizChecked && selected && isCorrect;
                          return (
                            <button
                              key={oid}
                              className="btn-card"
                              onClick={() => handleSelectQuizAnswer(oid)}
                              style={{
                                textAlign: 'left',
                                borderColor: isCorrectSelected
                                  ? '#16a34a'
                                  : isWrongSelected
                                    ? '#dc2626'
                                    : selected
                                      ? '#2563eb'
                                      : '#cbd5e1',
                                background: isCorrectSelected
                                  ? '#ecfdf5'
                                  : isWrongSelected
                                    ? '#fef2f2'
                                    : selected
                                      ? '#eff6ff'
                                      : '#ffffff'
                              }}
                            >
                              <strong style={{ marginRight: 8 }}>{oid.toUpperCase()}.</strong>
                              {opt?.text}
                            </button>
                          );
                        })}
                      </div>
                      <div className="lesson-action-ui" style={{ marginTop: 12 }}>
                        {quizChecked && (
                          <button
                            className="btn-card lesson-action-primary"
                            onClick={handleNextQuizQuestion}
                          >
                            {quizIndex < quizQuestions.length - 1
                              ? (language === 'ro' ? 'Următoarea întrebare' : 'Next question')
                              : (language === 'ro' ? 'Finalizează quiz-ul' : 'Finish quiz')}
                          </button>
                        )}
                      </div>
                      {quizChecked && currentQuizQuestion.explanation && (
                        <div
                          className="lesson-feedback"
                          style={{
                            marginTop: 10,
                            borderColor:
                              quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                                ? '#16a34a'
                                : '#dc2626',
                            background:
                              quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                                ? '#ecfdf5'
                                : '#fef2f2',
                            color:
                              quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                                ? '#14532d'
                                : '#7f1d1d'
                          }}
                        >
                          {currentQuizQuestion.explanation}
                        </div>
                      )}
                    </>
                  )}
                  {quizCompleted && (
                    <div
                      className="lesson-feedback"
                      style={{
                        borderColor: quizPassed ? '#16a34a' : '#dc2626',
                        background: quizPassed ? '#ecfdf5' : '#fef2f2',
                        color: quizPassed ? '#14532d' : '#7f1d1d'
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {language === 'ro'
                          ? `Quiz finalizat: ${quizCorrectCount}/${quizQuestions.length} (${quizScorePct}%)`
                          : `Quiz completed: ${quizCorrectCount}/${quizQuestions.length} (${quizScorePct}%)`}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        {language === 'ro'
                          ? `Pragul de trecere: ${quizPassScore}%.`
                          : `Passing score: ${quizPassScore}%.`}
                      </div>
                      {!quizPassed && (
                        <>
                          <div style={{ marginTop: 8 }}>
                            {language === 'ro'
                              ? `Ai sub ${quizPassScore}%. Lecția rămâne neterminată. Refă quiz-ul ca să activezi butonul Terminat.`
                              : `You are below ${quizPassScore}%. The lesson remains unfinished. Retry the quiz to enable the Done button.`}
                          </div>
                          <div className="lesson-action-ui" style={{ marginTop: 12 }}>
                            <button
                              className="btn-card lesson-action-primary"
                              onClick={handleRetryQuiz}
                            >
                              {language === 'ro' ? 'Refă quiz-ul' : 'Retry quiz'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="lesson-controls">
            <button 
              className="btn-nav btn-previous" 
              onClick={handlePrevious}
              disabled={currentSegment === 0}
            >
              ← {language === 'ro' ? 'Anterior' : 'Previous'}
            </button>
            
            {isLastSegment ? (
              <button
                className="btn-nav btn-next"
                onClick={handleDone}
                disabled={doneDisabled}
              >
                {isPuzzleFlow
                  ? (language === 'ro' ? 'Continuă antrenamentul' : 'Continue training')
                  : (language === 'ro' ? 'Terminat' : 'Done')} →
              </button>
            ) : (
              <button
                className="btn-nav btn-next"
                onClick={handleNext}
                disabled={nextDisabled}
              >
                {(language === 'ro' ? 'Următorul' : 'Next')} →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Audio placeholder - can be added later */}
      <div className="audio-placeholder" style={{ display: 'none' }}>
        <audio controls>
          <source src={segment?.audioUrl} type="audio/mpeg" />
        </audio>
      </div>
    </div>
  );
}

export default LessonViewer;
